module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h1', {
    staticClass: "chapter-no",
    attrs: {
      "id": "ch-1"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_1"
    }
  }, [_c('strong', [_vm._v("CHAPTER 1")])])]), _c('a', {
    attrs: {
      "id": "page_1"
    }
  }, [_c('h1', {
    staticClass: "chapter-title"
  }, [_c('em', [_vm._v("Issues in Preaching Christ from Genesis")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("Before preaching the Genesis narratives, preachers should take note of some of the problems they will encounter and how they may be able to resolve them. This introductory chapter will first review issues involved in preaching Christ, next, reasons for selecting Genesis narratives as preaching-texts, third, issues in interpreting Genesis, and finally, issues in preaching these narratives.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-1"
    }
  }, [_c('strong', [_vm._v("Issues in Preaching Christ")])])]), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_1"
    }
  }, [_vm._v("This book is about preaching Christ from the Genesis narratives. Unfortunately, there is much confusion about what preaching Christ means precisely."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_1",
      "id": "fn-1"
    }
  }, [_vm._v("1")]), _vm._v(" On the basis of New Testament testimony, I have previously defined preaching Christ as “preaching sermons which authentically integrate the message of the text with the climax of God’s revelation in the person, work, and/or teaching of Jesus Christ as revealed in the New Testament.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_2",
      "id": "fn-2"
    }
  }, [_vm._v("2")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-1"
    }
  }, [_c('strong', [_c('em', [_vm._v("Two Hermeneutical Moves")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The above definition of preaching Christ assumes two moves in interpretation. First, it assumes that the interpreter understands "), _c('a', {
    attrs: {
      "id": "page_2"
    }
  }, [_vm._v("the message of the text in its own historical context, that is, the interpreter seeks to discern the message the writer wanted Israel to hear."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_3",
      "id": "fn-3"
    }
  }, [_vm._v("3")]), _vm._v(" Second, it assumes that the interpreter understands the message of the text in the contexts of the whole canon (including the New Testament) and all of redemptive history."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_4",
      "id": "fn-4"
    }
  }, [_vm._v("4")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("There are two major reasons for this move to the New Testament. First, a crucial hermeneutical rule is that a text must always be understood in its context. The context of the Genesis narratives in the Christian Bible, of course, is not just the Old Testament but also the New Testament. For example, when the narrative about God’s covenant with Abraham (Gen 17) contains God’s commandment: “Every male among you shall be circumcised” (17:10), preachers instinctively sense that they cannot apply this commandment directly to the congregation today. Rather, they understand this text in the context of the New Testament, where the Holy Spirit guided the church to replace circumcision as a sign of God’s covenant (Acts 15:28–29) with the New Testament sign of baptism (Col 2:11–14). Similarly, when a narrative contains a promise of the coming Messiah, such as Genesis 3:15 about the seed of the woman, preachers cannot stop at the promise but must go on to its fulfillment in Jesus Christ. Or, when a narrative contains a type of Christ, such as Genesis 22:13 about the ram offered “instead of Isaac,” preachers cannot stop at the type but must go on the Antitype, Jesus Christ, who was offered “instead of” God’s people.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("A second reason for this move to the New Testament is the New Testament requirement that Christian preachers preach Jesus Christ. Christian preaching is different from Jewish preaching precisely because Christian preachers understand Old Testament passages in the context of God’s further revelation in the New Testament. As Graeme Goldsworthy aptly puts it: “The story is never complete in itself and belongs as part of the one big story of salvation culminating in Jesus Christ. Simply telling a story based on a piece of Old Testament historical narrative, however complete in itself, is not Christian preaching.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_5",
      "id": "fn-5"
    }
  }, [_vm._v("5")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-2"
    }
  }, [_c('strong', [_c('em', [_vm._v("Seven Ways of Preaching Christ")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Throughout the church’s history, preachers have preached Christ from the Old Testament in various ways. Sometimes they used ways that we today would consider illegitimate, such as allegorizing and typologizing."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_6",
      "id": "fn-6"
    }
  }, [_vm._v("6")])]), _vm._v(" In "), _c('em', [_vm._v("Preaching Christ from the Old Testament")]), _vm._v(" I identified seven ways in which preachers can move legitimately from the periphery to the center, from the Old Testament passage to Jesus Christ in the New Testament."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_7",
      "id": "fn-7"
    }
  }, [_vm._v("7")])]), _vm._v(" These seven "), _c('a', {
    attrs: {
      "id": "page_3"
    }
  }, [_vm._v("ways are: redemptive-historical progression, promise-fulfillment, typology, analogy, longitudinal themes, New Testament references, and contrast. We shall briefly review each of these roads to Christ in the New Testament.")])]), _c('a', {
    attrs: {
      "id": "page_3"
    }
  }, [_c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-1"
    }
  }, [_c('em', [_vm._v("Redemptive-Historical Progression")])])]), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_3"
    }
  }, [_vm._v("The broadest and foundational path from an Old Testament text to Jesus Christ is the way of redemptive-historical progression. It traces God’s history with the world from his good creation (Gen 1), to the human fall into sin and God’s plan of redemption through the seed of the woman (Gen 3:15), to a long history of God continuing the line of the seed of the woman (Gen 3–Malachi), to Christ (the Gospels), the church (Acts and New Testament Letters), and finally to the new creation (Rev 22). In other words, in the Bible we can trace a continuous redemptive history which centers in the life, death, and resurrection of Jesus Christ, who then ascends to rule his church from heaven until he comes again. In broad strokes, we can picture redemptive history as follows:"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_8",
      "id": "fn-8"
    }
  }, [_vm._v("8")])]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/3.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("When a text witnesses to God’s saving activity in history, we find ourselves on a road that progresses steadily through the Old Testament, leads to God’s ultimate saving act in the sending of his Son, Jesus Christ, and culminates in Jesus’ victorious Second Coming. For example, when one is preaching on the narrative of Cain murdering Abel (Gen 4), the fact that God provides Seth “instead of Abel” (4:25) to continue the line of the seed of the woman constitutes a link in the chain of redemptive-historical progression that leads to the birth of Jesus Christ, "), _c('em', [_vm._v("the")]), _vm._v(" Seed of the woman (Gal 3:16), and ultimately to his victorious Second Coming.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In the following chapters we will trace the plot lines of each individual narrative. It may be helpful here to visualize the Bible’s meta-narrative as a single plot:")]), _vm._v(" "), _c('p', {
    staticClass: "para-ceni"
  }, [_c('strong', [_vm._v("The Bible’s Meta-Narrative")])]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('a', {
    attrs: {
      "id": "page_4"
    }
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "id": "fn-9",
      "src": "images/4.jpg"
    }
  })])]), _c('a', {
    attrs: {
      "id": "page_4"
    }
  }, [_c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-2"
    }
  }, [_c('em', [_vm._v("Promise-Fulfillment")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("A more direct road to Christ from an Old Testament text is the way of promise-fulfillment. If the text contains a promise of the coming Messiah, the preacher can move to the New Testament to show the ultimate fulfillment of that promise in Jesus Christ. For example, in Jacob’s last words to his sons, his words to Judah form a messianic promise:")]), _vm._v(" "), _c('p', {
    staticClass: "poem-linet"
  }, [_vm._v("“The scepter shall not depart from Judah,")]), _vm._v(" "), _c('p', {
    staticClass: "poem-line1"
  }, [_vm._v("nor the ruler’s staff from between his feet,")]), _vm._v(" "), _c('p', {
    staticClass: "poem-line"
  }, [_vm._v("until tribute comes to him;")]), _vm._v(" "), _c('p', {
    staticClass: "poem-line1"
  }, [_vm._v("and the obedience of the peoples is his.”")]), _vm._v(" "), _c('p', {
    staticClass: "poem-author"
  }, [_vm._v("(Gen 49:10)")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("In preaching this narrative, one can easily move from this Old Testament promise to its New Testament fulfillment in Jesus Christ, the King of kings born of the tribe of Judah and the house of David (Matt 1:1–17).")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-3"
    }
  }, [_c('em', [_vm._v("Typology")])])]), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_4"
    }
  }, [_vm._v("A third road from an Old Testament text to Christ is typology. Old Testament redemptive events, persons, or institutions can function as types which foreshadow the great Antitype, the person and/or work of Jesus Christ. For example, if one preaches on the Fall of Adam and Eve (Gen 3), one notes that the first Adam as representative of the human race prefigures the second Adam, Jesus. ")]), _c('a', {
    attrs: {
      "id": "page_5"
    }
  }, [_vm._v("In fact, Paul calls Adam “a type "), _c('em', [_vm._v("(typos)")]), _vm._v(" of the one who was to come” (Rom 5:14)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_10",
      "id": "fn-10"
    }
  }, [_vm._v("10")]), _vm._v(" Adam is an antithetic (death vs. life) type (representing humanity) of the Antitype, Jesus Christ.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-4"
    }
  }, [_c('em', [_vm._v("Analogy")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("A fourth and more general road from an Old Testament text to Christ is the way of analogy. Analogy exposes parallels between what God taught Israel and what Christ teaches the church; what God promised Israel and what Christ promises the church; what God demanded of Israel (the law) and what Christ demands of his church. For example, if the message of Genesis 12:1–9 is that Israel must claim Canaan for the worship of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(", one can use analogy to move to the New Testament and show that Jesus mandates his church to claim all nations for God (Matt 28:18–20).")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-5"
    }
  }, [_c('em', [_vm._v("Longitudinal Themes")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("A fifth road from an Old Testament text to Christ is the way of longitudinal themes. While this way will often overlap with redemptive-historical progression,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_11",
      "id": "fn-11"
    }
  }, [_vm._v("11")])]), _vm._v(" it is distinct in focusing on the development of theological ideas rather than development in redemptive history. “Longitudinal themes” is a technical term in the discipline of Biblical Theology. It refers to themes that can be traced through the Scriptures from the Old Testament to the New—themes such as God’s coming kingdom, God’s covenant, God’s redemption, God’s presence, God’s love, God’s faithfulness, God’s grace, God’s judgment, God’s providence. We can utilize this concept of longitudinal themes for preaching Christ because every major Old Testament theme leads to Christ. For example, the theme of God’s law can be traced from the creation account (Gen 2:16–17), through the patriarchs, to Sinai, to the prophets, and to Christ, who not only lived God’s law perfectly and thus fulfilled the law for us, but who also showed us the depth-dimension of God’s law and expects us to live by it (Matt 5–7; 22:37–40).")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-6"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_6"
    }
  }, [_c('em', [_vm._v("New Testament References")])])]), _c('a', {
    attrs: {
      "id": "page_6"
    }
  }), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_6"
    }
  }, [_vm._v("A sixth road from an Old Testament text to Christ is that of New Testament references. Sometimes the New Testament alludes to or quotes the selected preaching-text and links it to Christ. In this case, the New Testament citation may possibly serve as a bridge to Christ. For example, if one preaches on the creation account of Genesis 1, the message for Israel is that the King of the universe by his Word created the earth as his good kingdom. After explaining the significance of God creating by his "), _c('em', [_vm._v("word")]), _vm._v(" (ten times “God said”), one can move to the New Testament, where John picks up on this “word” "), _c('em', [_vm._v("(logos)")]), _vm._v(" and identifies it as Jesus. In fact he quotes Genesis 1:1 when he writes, “"), _c('em', [_vm._v("In the beginning")]), _vm._v(" was the Word.… All things came into being through him …” (John 1:1, 3). This New Testament reference by itself is sufficient to function in the sermon as a bridge to Christ. Most often, however, New Testament references can best be used to support one of the other roads to Christ."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_12",
      "id": "fn-12"
    }
  }, [_vm._v("12")])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-7"
    }
  }, [_c('em', [_vm._v("Contrast")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("A final road from the Old Testament to Christ is the way of contrast. Because of the coming of Christ the text’s message for the contemporary church may be quite different from the original message for Israel. For example, God commanded Abraham/Israel to circumcise every male among them as a sign of covenant membership (Gen 17:11). For two thousand years circumcision functioned as God’s sign of covenant membership for Israel. But for Gentile converts the first council of the Christian church repealed this ancient ordinance with its shedding of blood (Acts 15:28–29). Instead of circumcision, baptism gradually became the sign of covenant membership (Col 2:11–14). The contrast between circumcision and baptism exists because of Jesus Christ, who shed his blood once for all, thereby ending the bloody rites and sacrifices of the old covenant.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-2"
    }
  }, [_c('strong', [_vm._v("Reasons for Selecting Genesis")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("To demonstrate the Christocentric method of interpretation and preaching I have selected the book of Genesis. There are several reasons for this choice. The most weighty reason is the importance of Genesis for the church; a second reason is the current lack of preaching from Genesis; and third, the challenge of preaching Christ from Genesis.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-3"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_7"
    }
  }, [_c('strong', [_c('em', [_vm._v("The Importance of Genesis for Christian Understanding")])])])]), _c('a', {
    attrs: {
      "id": "page_7"
    }
  }), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_7"
    }
  }, [_vm._v("The primary reason for selecting the book of Genesis to demonstrate the Christocentric method is the crucial importance of this book for the Christian church. For Genesis provides Christians with a worldview that is assumed but not necessarily taught by later Scriptures. If we fail to preach Genesis, we deprive our congregations in a large measure of this foundational worldview. A worldview is “a consistent conception of all existence” (Webster), or, better, “the comprehensive framework of one’s basic beliefs about things.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_13",
      "id": "fn-13"
    }
  }, [_vm._v("13")]), _vm._v(" It is a foundational view of all of reality that enables us to see individual elements and events in the light of the whole. Basically, a worldview consists of the knowledge of three entities and their interrelationship: God, the cosmos, and human beings.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/7.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("Genesis teaches that God is sovereign (vs. atheism) and wholly other than this universe (vs. pantheism), the only true God (vs. polytheism), the Creator of this universe (vs. secularism and naturalistic evolutionism), who has made a covenant with his creation and upholds it (vs. deism), and made human beings in his image to manage the world on his behalf (vs. hedonism). Genesis further teaches that God is not the source of evil but created everything good so that his creatures can enjoy the physical world (vs. gnosticism). The source of the evil and brokenness we see in the world today is human disobedience to God’s command, which resulted in God’s curse on creation. But God immediately promised to restore his creation to the beautiful kingdom he intended it to be. And so Genesis sketches the beginnings of redemptive history: God seeking to restore his creation and creatures, finally making a special covenant with Abram, who was to be a blessing to “all the families of the earth” (Gen 12:3).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("As Genesis sketches the beginnings of redemptive history, it teaches about God’s coming kingdom, God’s love for his creation and his creatures, God’s judgment of sin, God’s grace for sinners, God’s covenant faithfulness, God’s sovereign providence, and God’s presence with his people. As Ken Mathews puts it: “If we possessed a Bible without Genesis, we would have a ‘house of cards’ without foundation or mortar. We cannot insure the continuing fruit of "), _c('a', {
    attrs: {
      "id": "page_8"
    }
  }, [_vm._v("our spiritual heritage if we do not give place to its roots.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_14",
      "id": "fn-14"
    }
  }, [_vm._v("14")]), _vm._v(" The church today needs to get in touch with its roots. After TV, videos, DVDs, pop music, and magazines have bombarded God’s people for a whole week with a worldview that excludes God, God’s people need “a reality check”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_15",
      "id": "fn-15"
    }
  }, [_vm._v("15")])]), _vm._v(" on Sunday, that is, sermons that expound the biblical worldview. God’s people need to hear more sermons from the book of Genesis.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-4"
    }
  }, [_c('strong', [_c('em', [_vm._v("The Lack of Preaching from Genesis")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("In selecting preaching-texts for their congregations, preachers have many reasons to avoid the book of Genesis."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_16",
      "id": "fn-16"
    }
  }, [_vm._v("16")])]), _vm._v(" One author characterizes the Genesis narratives as follows: “They seem to be a conglomeration of very secular legends that have little if anything to do with faith. Their content (rape, murder, strife and jealousy between brothers, two unsatisfied wives fighting over the sexual attentions of their husband, and cunning deception within families) seems more appropriate to afternoon soap operas than God’s revealed word.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_17",
      "id": "fn-17"
    }
  }, [_vm._v("17")])]), _vm._v(" Moreover, ever since Wellhausen, source criticism has carved up the book into debatable J, E, P, D, and more segments. This fragmentation makes the preaching-text uncertain and loses sight of the final author’s message for Israel. Furthermore, in preaching Genesis, preachers are currently confronted with such troublesome questions as: Is the earth young or old? Were the days in Genesis 1 twenty-four-hour days or long periods of time? Was the flood universal or local? Were Abraham, Isaac, and Jacob historical figures or fictional characters? Because of this combination of hermeneutical, homiletical, and pastoral problems, modern preachers may think it the better part of wisdom to avoid preaching the Genesis narratives.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-5"
    }
  }, [_c('strong', [_c('em', [_vm._v("The Challenge of Preaching Christ from Genesis")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("A third reason for selecting Genesis is the challenge of preaching Christ from Genesis. Genesis is said to contain only seven “messianic texts,” where one can employ the promise-fulfillment scheme. The traditional messianic texts listed for Genesis are:")]), _vm._v(" "), _c('p', {
    staticClass: "para-lt"
  }, [_c('a', {
    attrs: {
      "id": "page_9"
    }
  }, [_vm._v("Genesis 3:15, the seed of the woman;")])]), _c('a', {
    attrs: {
      "id": "page_9"
    }
  }, [_c('p', {
    staticClass: "para-l"
  }, [_vm._v("Genesis 4:25, the line of Seth;")]), _vm._v(" "), _c('p', {
    staticClass: "para-l"
  }, [_vm._v("Genesis 9:26, the blessing of Shem;")]), _vm._v(" "), _c('p', {
    staticClass: "para-l"
  }, [_vm._v("Genesis 12:3, the blessing of Abraham for “all the families of the earth”;")]), _vm._v(" "), _c('p', {
    staticClass: "para-l"
  }, [_vm._v("Genesis 26:3, the blessing of Isaac’s seed;")]), _vm._v(" "), _c('p', {
    staticClass: "para-l"
  }, [_vm._v("Genesis 46:3, the promise to Jacob; and")]), _vm._v(" "), _c('p', {
    staticClass: "para-l"
  }, [_vm._v("Genesis 49:10, the promise of kingly rule to Judah.")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("To preach Christ from these passages may seem fairly straightforward. But how does one preach Christ from the many other narratives in Genesis?")])]), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_9"
    }
  }, [_vm._v("Preachers have tried to solve this problem in various ways. Some simply identified “Jehovah” with Christ, that is, whenever the text mentions Yahweh, we can substitute Christ."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_18",
      "id": "fn-18"
    }
  }, [_vm._v("18")]), _vm._v(" Others, taking their cue from John 1 (“In the beginning was the Word”), look for the eternal Logos at work in the Old Testament. In Genesis, they claim, the Angel of Yahweh is the eternal Logos, the second person of the Trinity."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_19",
      "id": "fn-19"
    }
  }, [_vm._v("19")])]), _vm._v(" But this is not very helpful, for the Angel of Yahweh appears in only a few passages. Moreover, identifying Christ with the Angel of Yahweh is not preaching Christ as the fullness of God’s revelation in Jesus of Nazareth. Still others seek to preach Jesus by using allegorical interpretation: they simply import the New Testament story of Jesus into the Old Testament without doing justice to the Old Testament message (a form of eisegesis). Still others seek to preach Jesus by understanding mere narrative "), _c('em', [_vm._v("details")]), _vm._v(" as types of Christ (the error of typologizing in distinction from proper typology).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("A few examples of flawed efforts may help disclose the difficulties preachers face in preaching Christ from the Genesis narratives. A popular radio preacher presented the following allegorical interpretation of Genesis 2:18–25:")]), _vm._v(" "), _c('p', {
    staticClass: "ext"
  }, [_vm._v("While Adam slept, God created from his wounded side a wife, who was part of himself, and he paid for her by the shedding of his blood.… Now all is clear. Adam is a picture of the Lord Jesus, who left His Father’s house to gain His bride at the price of His own life. Jesus, the last Adam, like the first, must be put to sleep to purchase His Bride, the Church, and Jesus died on the cross and slept in the tomb for three days and three nights. His side too "), _c('a', {
    attrs: {
      "id": "page_10"
    }
  }, [_vm._v("was opened after He had fallen asleep, and from that wounded side redemption flowed."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_20",
      "id": "fn-20"
    }
  }, [_vm._v("20")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Others moved from the mark of protection God put on the murderer Cain (Gen 4:15) to the cross of Christ, speculating that Cain’s mark was “in the form of a cross.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_21",
      "id": "fn-21"
    }
  }, [_vm._v("21")])]), _vm._v(" Similarly, “In Melchisedek’s bringing of bread and wine [Gen 14] we have a clear allusion to the sacrament of the New Covenant which Jesus instituted for the completion and dissolution of the old.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_22",
      "id": "fn-22"
    }
  }, [_vm._v("22")])]), _vm._v(" Many have preached Isaac carrying the wood up the mountain (Gen 22:6) as a type of Christ carrying his cross."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_23",
      "id": "fn-23"
    }
  }, [_vm._v("23")])]), _vm._v(" One modern dictionary of types moves allegorically to Christ from the narrative of Abraham sending his servant to find a wife for Isaac (Gen 24):")]), _vm._v(" "), _c('p', {
    staticClass: "ext"
  }, [_vm._v("Abraham is a type of the Father who sent His servant (the Spirit) to obtain a bride (Rebecca) for his son Isaac. The servant represents the Holy Spirit, and Isaac represents the Lord Jesus Christ.… Rebecca represents the Church."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_24",
      "id": "fn-24"
    }
  }, [_vm._v("24")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Still others have preached the details of Joseph’s life as the life of Christ:")]), _vm._v(" "), _c('p', {
    staticClass: "ext"
  }, [_vm._v("I need not say to you, beloved, who are conversant with Scripture, that there is scarcely any personal type in the Old Testament which is more clearly and fully a portrait of our Lord Jesus Christ than is the type of Joseph. You may run the parallel between Joseph and Jesus in very many directions.… In making himself known to his brethren, he was a type of our Lord revealing himself to us.… I. Notice, first, that the Lord Jesus Christ, like Joseph, reveals himself in private for the most part.… II. The second remark I have to make is this,—when the Lord Jesus Christ reveals himself to any man for the first time, it is usually in the midst of terror, and that first revelation often creates much sadness.… III. Now, thirdly, though the first appearance of Jesus, like that of Joseph, may cause sadness, the further revelation of the Lord Jesus Christ to his brethren, brings them the greatest possible joy."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_25",
      "id": "fn-25"
    }
  }, [_vm._v("25")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("These flawed efforts illustrate the sincere desire as well as the difficulty of authentically preaching Christ from the Genesis narratives. However, if the following "), _c('a', {
    attrs: {
      "id": "page_11"
    }
  }, [_vm._v("essays can demonstrate that one or more of the seven ways of preaching Christ enables us legitimately to preach Christ from the Genesis narratives, we should be able to preach Christ from almost any Old Testament book."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_26",
      "id": "fn-26"
    }
  }, [_vm._v("26")])]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-3"
    }
  }, [_c('strong', [_vm._v("Issues in Interpreting Genesis")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Before proceeding to the expository essays, we need to explore some crucial issues in interpreting the book of Genesis. We shall first discuss issues in literary interpretation and then move on to issues in historical interpretation.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-6"
    }
  }, [_c('strong', [_c('em', [_vm._v("Issues in Literary Interpretation")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-8"
    }
  }, [_c('em', [_vm._v("Source Criticism")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Source criticism was first called “literary criticism.” Most modern commentaries on Genesis will acquaint students with the Graf-Wellhausen Documentary Hypothesis of J, E, P, and D sources, which critical scholarship “widely accepted from about 1878 to 1970, [though] there have been significant dissenters at various points.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_27",
      "id": "fn-27"
    }
  }, [_vm._v("27")])]), _vm._v(" It was held that J (Yahwist) dated from Israel’s monarchy around 950 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.")]), _vm._v(", E (Elohist) from the time of the divided kingdom around 850 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.")]), _vm._v(", D (Deuteronomist) from shortly before the exile around 620 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.")]), _vm._v(", and P (Priestly Code) from after the exile around 500 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C")]), _vm._v(". The result of this Documentary Hypothesis was the rejection of the traditional Jewish and Christian position that Moses was the primary author of the Pentateuch since Moses would predate these sources by at least 300 years. Moreover, with most of its efforts going into source criticism, Old Testament scholarship largely abandoned the church and its preachers, for preachers do not preach hypothetical sources behind the text but the final text the church received in its canon as the inspired Scriptures (2 Tim 3:16–4:2).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Happily, after a century of exploring blind alleys, “the hypothetical character of the results of modern criticism” was finally acknowledged"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_28",
      "id": "fn-28"
    }
  }, [_vm._v("28")])]), _vm._v(" and the "), _c('a', {
    attrs: {
      "id": "page_12"
    }
  }, [_vm._v("new literary criticism of the last few decades returned scholarly concentration to the final text. In fact, the new literary criticism has undermined the Documentary Hypothesis. Gary Rendsburg has shown that “the same or similar vocabulary appears in matching units which are usually assigned to different sources.” He concludes, “All of this material demonstrates how attention to redactional structuring greatly weakens the Documentary Hypothesis, indeed according to the present writer, renders it untenable.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_29",
      "id": "fn-29"
    }
  }, [_vm._v("29")]), _vm._v(" With the collapse of the Documentary Hypothesis, its dating of the sources of Genesis also collapses."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_30",
      "id": "fn-30"
    }
  }, [_vm._v("30")])]), _vm._v(" Gordon Wenham comments, “Without denying the presence of sources within the narrative, the new literary critic wants to understand how the final editor viewed his material and why he arranged it in the way he did.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_31",
      "id": "fn-31"
    }
  }, [_vm._v("31")])]), _vm._v(" This new literary criticism, as well as its precursors such as redaction criticism and rhetorical criticism, can help preachers discern the message of the preaching-text."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_32",
      "id": "fn-32"
    }
  }, [_vm._v("32")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-9"
    }
  }, [_c('em', [_vm._v("The")]), _vm._v(" Tôlĕdôt "), _c('em', [_vm._v("Structure of Genesis")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Genesis is frequently divided into two distinct parts:")]), _vm._v(" "), _c('p', {
    staticClass: "num-listt"
  }, [_vm._v("1."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Primeval History (Gen 1:1–11:26), and")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_vm._v("2."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Patriarchal History (Gen 11:27–50:26).")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Although God’s call of Abram clearly begins a new section in Genesis, for purposes of proper interpretation and preaching it is crucial also to see the overall literary structure of this book. Awareness of the unity of Genesis will enable us to interpret each narrative in the light of the whole book.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The most obvious literary feature unifying the Genesis narratives is the author’s carefully crafted "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" structure. "), _c('em', [_c('span', {
    staticClass: "char-i"
  }, [_vm._v("ʾ")]), _vm._v("Ēlleh tôlĕdôt")]), _vm._v(" is variously translated in our English versions as: “These are the generations of …”; or, “These are the descendants of …”; or, “This is the history of.…” Tôlĕdôt “stands for that "), _c('a', {
    attrs: {
      "id": "page_13"
    }
  }, [_vm._v("which was produced, for the result.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_33",
      "id": "fn-33"
    }
  }, [_vm._v("33")]), _vm._v(" By way of "), _c('em', [_vm._v("ten")]), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_34",
      "id": "fn-34"
    }
  }, [_vm._v("34")])]), _vm._v(" sets of generations, the author traces Israel’s roots back from enslavement in Egypt to Adam and Eve in Paradise.")]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-1"
    }
  }, [_c('strong', [_vm._v("Ten Sets of "), _c('em', [_vm._v("Tôlĕdôt")])]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Genesis begins with God creating his good kingdom on earth in "), _c('em', [_vm._v("seven")]), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_35",
      "id": "fn-35"
    }
  }, [_vm._v("35")])]), _vm._v(" days, and the first "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" (of heaven and earth, Gen 2:4–4:26) shows what happened to that good kingdom: human rebellion against God, banishment from Paradise, Cain murdering his brother Abel, and the depth of depravity in the "), _c('em', [_vm._v("seventh")]), _vm._v(" generation when Lamech boasts of killing a young man for striking him (4:23). But God gives Adam and Eve another child “instead of Abel” (4:25), Seth, and the line of the seed of the woman can continue.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The second "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is the "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" of Adam (Gen 5:1–6:8). In "), _c('em', [_vm._v("ten")]), _vm._v(" generations it runs from Adam through Seth to Noah. This is the line of the seed of the woman. The "), _c('em', [_vm._v("seventh")]), _vm._v(" generation in this line is the polar opposite of Lamech, the seventh generation in the first "), _c('em', [_vm._v("tôlĕdôt:")]), _vm._v(" “Enoch walked with God; then he was no more, because God took him” (5:24). And the tenth generation is Noah, of whom his father said, “Out of the ground that the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" has cursed this one shall bring us relief from our work and from the toil of our hands” (5:29). Yet in the time of Noah such evil prevails that God decides to “blot out from the earth the human beings I have created” (6:7). “But Noah found favor in the sight of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("” (6:8). Again the line of the seed of the woman can continue.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The third "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is that of Noah (6:9–9:29). “Noah walked with God. And Noah had three sons, Shem, Ham, and Japheth” (6:9–10). But “the earth was corrupt in God’s sight, and the earth was filled with violence” (6:11). God instructs Noah to build an ark so that a remnant can survive the great flood that is about to scour and cleanse the earth. After the flood subsides, God makes a covenant with the earth and with Noah and his sons. “God blessed Noah and his sons, and said to them, ‘Be fruitful and multiply, and fill the earth’ ” (9:1). Noah is another Adam starting out on a clean earth. But evil still lurks in people’s hearts. Noah becomes drunk and lies naked in his tent. His son Ham dishonors him. When Noah discovers this, he curses Ham’s son Canaan (9:21–25). Canaan is another Cain—seed of the serpent. But Noah may also bless Shem and Japheth (9:26–27), and the line of the seed of the woman can continue.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The fourth "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is the "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" of Noah’s sons, Shem, Ham, and Japheth (10:1–11:19). Reversing the chronological order, it first lists seventy (10 × 7) nations that “spread abroad on the earth after the flood” (10:32) and then relates "), _c('a', {
    attrs: {
      "id": "page_14"
    }
  }, [_vm._v("the story of God confusing the language at Babel so that people will be scattered “abroad over the face of all the earth” (11:9). The author may well have changed the chronological order so that this fourth "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" parallels the first three by ending once again in human disobedience: (1) Cain cursed and Lamech’s detestable boast, (2) extreme violence, (3) Canaan cursed, and (4) Babel rebellion. With this repeated sequence the author calls attention to constant human rebellion and to God’s grace in making new starts to continue the line of the seed of the woman.")])]), _c('a', {
    attrs: {
      "id": "page_14"
    }
  }, [_c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The fifth "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is that of Shem (11:10–26). It moves quickly through the generations until it reaches another number “ten,” Abram and his brothers (cf. the second "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(", Adam to number “ten,” Noah).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The sixth "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is the "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" of Terah (11:27–25:11). It relates God’s call of Abram, the blessings promised, and God’s covenant with Abram. God makes a new start with Abram, who, like Noah, is another Adam. The line of the seed of the woman can continue. But Sarai is barren and, as was customary in that culture, gives her maid Hagar to Abram to raise up children. Ishmael is born (16:1–16). Later Sarah conceives and Isaac is born. Now Abraham has two sons, Ishmael and Isaac, but the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" tells him that “it is through Isaac that offspring [seed] shall be named for you” (21:12). This "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" concludes with the death of Sarah (ch. 23), the marriage of Isaac and Rebekah (ch. 24), and the death of Abram (ch. 25).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The seventh "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is that of Ishmael (25:12–18). With only seven verses, it is the shortest list. Since Ishmael is a son of Abraham, the author includes this "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(", but he can hardly wait to get back to the line of the seed of the woman.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The eighth "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is the "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" of Isaac (25:19–35:29). It deals briefly with Isaac but soon moves to the twins Esau and Jacob and concentrates especially on Jacob: his bargaining for the birthright from his older brother, deceiving his aged father for the blessing, his escape to uncle Laban, his marriage to two sisters, Leah and Rachel, and their maids, Bilhah and Zilpah, Jacob’s flight back to Canaan, his meeting with God at Peniel, and his reconciliation with brother Esau. This "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" ends with the death of Rachel, a listing of Jacob’s twelve sons, and the death of Isaac.")])]), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_14"
    }
  }, [_vm._v("The ninth "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is that of Esau (36:1–37:1)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_36",
      "id": "fn-36"
    }
  }, [_vm._v("36")]), _vm._v(" It is similar to the "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" of Ishmael. It seeks to give a brief account of the descendants of Esau, who is, after all, Isaac and Rebekah’s son. But the interest of the author lies with the chosen son Jacob and his descendants.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The tenth "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is the "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" of Jacob (37:2–50:26). As clearly as anywhere, "), _c('a', {
    attrs: {
      "id": "page_15"
    }
  }, [_vm._v("we see here that a "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" deals not primarily with the person named but the one begotten by that person: “This is the story of the family ("), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(") of Jacob. Joseph, being seventeen years old, was shepherding the flock with his brothers …” (37:2). This final "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" tells the story of Joseph being sold by his brothers, his imprisonment in Egypt, his rise to power, his testing of his brothers, and his arranging for Jacob and his family to settle in Goshen. The "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" ends with the last days of Jacob, his blessing of Joseph’s sons, prophesying to his own sons, Jacob’s death in Egypt and burial in Canaan, and Joseph’s death. Genesis ends with the words, “He [Joseph] was embalmed and placed in a coffin in Egypt” (50:26). The story that began with life in Paradise appears to end with death in Egypt. But this is not the end of the story. God has called a new generation of Israelites into being. Joseph’s last words are, “When God comes to you [to bring you to the land that he swore to Abraham, to Isaac, and to Jacob], you shall carry up my bones from here” (50:24–25). The diagram on ")]), _c('a', {
    attrs: {
      "href": "#/010_Chapter01#page_16"
    }
  }, [_vm._v("page 16")]), _vm._v(" will help visualize the author’s overall literary structure of Genesis and some of its intricacies.")]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-2"
    }
  }, [_c('strong', [_vm._v("Functions of the "), _c('em', [_vm._v("Tôlĕdôt")]), _vm._v(" Structure")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("The "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" structure fulfills several functions in Genesis. First, from Israel’s later perspective as a nation, the “genealogies display the existing relationship between kinship groups by tracing their lineage back to a common ancestor.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_37",
      "id": "fn-37"
    }
  }, [_vm._v("37")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Second, the linear genealogies of Genesis 5:1–31 and 11:10–26 (each ten generations) serve to link one narrative to another. They “establish continuity over stretches of time without narrative.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_38",
      "id": "fn-38"
    }
  }, [_vm._v("38")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Third, the genealogies, some individually and as a whole, mark the process of the narrowing of God’s channel of redemption. The first "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(", of heaven and earth, begins universally and ends with Seth, with whom God will continue the seed of the woman. The second "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(", of Adam, narrows the field from many people who are destroyed in the flood, to Seth’s descendant Noah, whom God selects to survive the devastation. The third "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" begins with Noah and his three sons, Shem, Ham, and Japheth, but ends with the blessing of Shem. Although the fourth "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is of Shem, Ham, and Japheth, the fifth narrows the line of the seed of the woman down to Shem. The sixth "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" begins with Terah and his three sons, Abram, Nahor, and Haran, but focuses on Abram as the recipient of God’s covenant promises. Abraham has two sons, Ishmael and Isaac, but Isaac is God’s chosen instrument. The seventh "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" about Ishmael’s "), _c('a', {
    attrs: {
      "id": "page_17"
    }
  }, [_vm._v("descendants is brief in order to put the full weight on Isaac’s descendants in the eighth "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(". Isaac also has two sons, Esau and Jacob, and again, the author spends little time on Esau’s descendants in the ninth tôlĕdôt, in order to concentrate on Jacob’s descendant, Joseph, in the final "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(".")])]), _c('a', {
    attrs: {
      "id": "page_17"
    }
  }), _c('p', {
    staticClass: "para-ceni"
  }, [_c('a', {
    attrs: {
      "id": "page_17"
    }
  }), _c('a', {
    attrs: {
      "id": "page_16"
    }
  }, [_c('strong', [_vm._v("Tôlĕdôt "), _c('em', [_vm._v("Structure of Genesis")])])])]), _c('a', {
    attrs: {
      "id": "page_16"
    }
  }, [_c('p', {
    staticClass: "bull-listt"
  }, [_vm._v("•"), _c('span', {
    staticClass: "pad"
  }, [_vm._v("A "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" discloses primarily what comes "), _c('em', [_vm._v("after")]), _vm._v(" the character(s) for whom the "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" is named.")])]), _vm._v(" "), _c('p', {
    staticClass: "bull-list"
  }, [_vm._v("•"), _c('span', {
    staticClass: "pad"
  }, [_vm._v("The "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" structure of Genesis begins universally with all creation, narrows down to all humanity, and narrows still further to one family and nation: Abram, Isaac, Jacob, and Israel.")])]), _vm._v(" "), _c('p', {
    staticClass: "bull-list"
  }, [_vm._v("•"), _c('span', {
    staticClass: "pad"
  }, [_vm._v("In its "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" structure Genesis highlights the numbers “ten,” the number of fullness, and “seven,” the number of perfection or completion.")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_c('strong', [_vm._v("“These are the generations of”:")])]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/16.jpg"
    }
  })])]), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_16"
    }
  }, [_vm._v("Finally, and most importantly, the genealogies reveal the sovereignty and the grace of God as he provides for the continuation of the line of the seed of the woman with a view to re-establishing his good kingdom on earth."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_39",
      "id": "fn-39"
    }
  }, [_vm._v("39")])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-10"
    }
  }, [_c('em', [_vm._v("Unifying Biblical-Theological Themes")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("There are also many biblical-theological themes that weave Genesis into a unified composition. We shall briefly note six intertwined strands: the kingdom of God, God’s blessing and curse, God’s covenant, covenant promises, the promise of seed, and the beginnings of redemptive history.")]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-3"
    }
  }, [_c('strong', [_vm._v("The Kingdom of God")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Genesis sketches the beginnings of the kingdom of God. Bruce Waltke observes, “Although the expression ‘kingdom of God’ never occurs in the Old Testament and its equivalents are relatively rare and late, the concept informs the whole. The Primary History, which traces Israel’s history from the creation of the world (Gen 1) to the fall of Israel (2 Kings 25), is all about what the New Testament calls ‘the kingdom of God.’ ”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_40",
      "id": "fn-40"
    }
  }, [_vm._v("40")])]), _vm._v(" Genesis 1 relates that God created this earth as his kingdom and that he created human beings in his image to manage this world on his behalf. Human beings were to obey the great King without question (Gen 2:15–17). Genesis 3 relates the tragic rebellion against God and God’s subsequent punishment, but also God’s resolve to restore his kingdom on earth (Gen 3:15). When Cain killed Abel, God continued the line of the seed of the woman with the birth of Seth. When terrible violence later made life impossible, God cleansed the earth with a flood and made a new start with Noah. When people subsequently disobeyed God at Babel, God confused their language and made a new start with Abram with a view to spreading his kingdom across the entire earth: “In you all the families of the earth shall be blessed” (Gen 12:3)—a promise subsequently repeated to Isaac (26:4) and Jacob (28:14).")])]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-4"
    }
  }, [_c('strong', [_vm._v("God’s Blessing and Curse")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("God promotes the cause of his kingdom by giving his blessing. The word “blessing/bless” "), _c('em', [_vm._v("(bĕrakâ/bārak)")]), _vm._v(" is used eighty-eight "), _c('a', {
    attrs: {
      "id": "page_18"
    }
  }, [_vm._v("times in Genesis, “more than in any other biblical book.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_41",
      "id": "fn-41"
    }
  }, [_vm._v("41")]), _vm._v(" The note of God’s blessing is first struck in Paradise. God blessed the animals (Gen 1:22) as well as humankind: “God blessed them, and God said to them, ‘Be fruitful and multiply, and fill the earth and subdue it; and have dominion …’ ” (1:28). Allen Ross succinctly explains the word “to bless”: “A study of its uses in Genesis shows that the giving of a blessing bestowed prosperity with respect to fertility of land and fertility of life. The gift of blessing included the empowerment to achieve what was promised.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_42",
      "id": "fn-42"
    }
  }, [_vm._v("42")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("With the Fall into sin, God’s curse entered the picture. God cursed the serpent as well as the ground (Gen 3:14, 17). “The curse in Genesis involved separation or alienation from the place of blessing, or even from those who were blessed.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_43",
      "id": "fn-43"
    }
  }, [_vm._v("43")])]), _vm._v(" God expelled Adam and Eve from his presence in the garden (3:24). Cain was “cursed from the ground” for murdering his brother (4:11). At the climax of the cycle of violence, God cursed the ground (see 8:21) with a great flood. And even though God blessed Noah and his sons and said to them as to Adam, “Be fruitful and multiply, and fill the earth” (9:1), Noah cursed his grandson Canaan and blessed his son Shem (9:25–26). Blessing and curse—which one will win out in the end?")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("God’s call of Abram leaves little doubt: “I will bless you, and make your name great, so that you will be a blessing. I will bless those who bless you, and the one who curses you I will curse; and in you all the families of the earth shall be blessed” (Gen 12:2–3). Many times God repeated these promised blessings to Abraham, to Isaac, and to Jacob."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_44",
      "id": "fn-44"
    }
  }, [_vm._v("44")])]), _vm._v(" In the book of Genesis we see a partial fulfillment of this universal promise of blessing especially in the life of Joseph, for the author records that “"), _c('em', [_vm._v("all the world")]), _vm._v(" came to Joseph in Egypt to buy grain, because the famine became severe throughout the world” (41:57). Under God’s blessing, the goodness of God’s original kingdom will eventually spread to “all the families of the earth.”")]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-5"
    }
  }, [_c('strong', [_vm._v("God’s Covenant")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("God administers his kingdom on earth by way of the covenant he makes with his creation and with his special people. Although the word “covenant” "), _c('em', [_vm._v("(bĕrît)")]), _vm._v(" is not used in the early chapters of Genesis, we can detect in them some of the usual elements of covenant treaties."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_45",
      "id": "fn-45"
    }
  }, [_vm._v("45")])]), _vm._v(" For example, in the "), _c('a', {
    attrs: {
      "id": "page_19"
    }
  }, [_vm._v("narrative about Paradise, a preamble identifies the great King as “the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" God” (Gen 2:4); a historical prologue recalls what the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" has done (2:7–15); covenant stipulations follow: “The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" God commanded the man, ‘You may freely eat of every tree of the garden; but of the tree of the knowledge of good and evil you shall not eat, for in the day that you eat of it you shall die’ ” (2:16–17); and finally there is God’s curse: “Cursed are you [the serpent].… Cursed is the ground” (3:14, 17).")])])]), _c('a', {
    attrs: {
      "id": "page_19"
    }
  }), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_19"
    }
  }, [_vm._v("The word "), _c('em', [_vm._v("bĕrît")]), _vm._v(" is first used when God announces that he will continue"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_46",
      "id": "fn-46"
    }
  }, [_vm._v("46")]), _vm._v(" his covenant with Noah, “But I will establish my covenant with you” (Gen 6:18). This is a covenant not only with Noah and his descendants but with “every living creature of all flesh” (9:9–17). In this covenant God promises “that never again shall all flesh be cut off by the waters of a flood” (9:11). The sign of God’s covenant with his creation is the rainbow (9:13).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Later God makes a special covenant with Abram, promising to give his descendants the land from the river Nile to the Euphrates (Gen 15:18). Still later God promises Abram: “I will make nations of you, and kings shall come from you. I will establish my covenant between me and you, and your offspring after you throughout their generations, for an everlasting covenant, to be God to you and to your offspring after you. And I will give to you, and to your offspring after you, the land where you are now an alien, all the land of Canaan, for a perpetual holding; and I will be their God” (17:6–8). The sign of this covenant is circumcision (17:10–14). This covenant of grace is extended to Isaac (17:19), to Jacob (35:11–12) and to all of Israel (Exod 6:2–8).")]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-6"
    }
  }, [_c('strong', [_vm._v("God’s Covenant Promises")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("The covenant, we have seen, is marked by God’s special promises. The repetition of these promises runs like a golden thread through Genesis. Frequently three major covenant promises are highlighted: a special relationship with God, numerous seed, and the land of Canaan. The fulfillment of all three promises will be required for establishing a theocratic kingdom in Canaan. These promises are repeated at various times in Genesis:")])]), _vm._v(" "), _c('p', {
    staticClass: "num-listt"
  }, [_c('a', {
    attrs: {
      "id": "page_20"
    }
  }, [_vm._v("1."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("The promise of a special relationship with God: 10 times.")])])]), _c('a', {
    attrs: {
      "id": "page_20"
    }
  }, [_c('p', {
    staticClass: "num-list"
  }, [_vm._v("2."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("The promise of numerous seed: 19 times.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_vm._v("3."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("The promise of land: 13 times.")])])]), _c('p', {
    staticClass: "num-list"
  }, [_c('a', {
    attrs: {
      "id": "page_20"
    }
  }, [_vm._v("4."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Allusions to God’s threefold promise: 17 times."), _c('sup')])]), _c('a', {
    attrs: {
      "href": "#/#fn_47",
      "id": "fn-47"
    }
  }, [_vm._v("47")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("Not to be overlooked, however, is God’s promise to Abram, “In you all the families of the earth shall be blessed” (Gen 12:3). This promise is all the more important because it comes at a crucial juncture: right at the end of God’s dealings with “all the families of the earth” (Gen 1–11) and at the beginning of God’s redemptive activity being channeled through one family (Abraham’s) and one nation (Israel). God tells Israel that he is not giving up on the nations; he will channel his grace through one family with a view to blessing “all the families of the earth.” This promise is repeated seven times in Genesis."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_48",
      "id": "fn-48"
    }
  }, [_vm._v("48")])])]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-7"
    }
  }, [_c('strong', [_vm._v("God’s Promise of Seed")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("The promise of seed is especially prominent in Genesis. The Old Testament uses the word “seed” "), _c('em', [_vm._v("(zera"), _c('span', {
    staticClass: "char-i"
  }, [_vm._v("ʿ")]), _vm._v(")")]), _vm._v(" 229 times, and 59 of these are in Genesis."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_49",
      "id": "fn-49"
    }
  }, [_vm._v("49")])]), _vm._v(" In Genesis 1 the word is first used for the seed of plants and fruit trees, but in Genesis 3:15 the word “seed” takes on a deeper, spiritual dimension as well. God says to the serpent: “I will put enmity between you and the woman, and between your offspring [seed] and hers.” Like plants and trees, human beings will also produce seed, but this seed will be of two kinds: the seed of the serpent and the seed of the woman; those who rebel against the great King and those who seek to follow God in obedience. Genesis will follow the development of these two kinds of seed, tracing especially the line of the seed of the woman, whose continued existence often appears in doubt: Abel is killed (4:8); Sarai is barren (11:30); Rebekah is barren (25:21); Rachel is barren (29:31); Jacob and his family are about to starve in Canaan (42:2). But in his grace, God continually intervenes so that the seed of the woman can advance from Adam and Eve to Seth, to Noah, to Abram, to Isaac, to Jacob, and, by the end of Genesis, to the beginning of numerous seed—the full number of 70 (10 × 7) people (Gen 46:27; Exod 1:5).")])]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-8"
    }
  }, [_c('strong', [_vm._v("The Beginnings of Redemptive History")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Our final biblical-theological theme in discerning the unified structure of Genesis doubles back to the first, the beginnings of the kingdom of God. Genesis 1 tells of God creating this earth as his "), _c('a', {
    attrs: {
      "id": "page_21"
    }
  }, [_vm._v("beautiful kingdom, but Genesis 3 relates the tragic rebellion against God, the King, and God’s punishment of humanity. However, God resolves to restore his kingdom on earth; he breaks up the unholy alliance between Adam/Eve and the serpent. God says to the serpent, “I will put enmity between you and the woman, and between your offspring [seed] and hers; he will strike your head, and you will strike his heel” (Gen 3:15). This declaration of enmity against Satan signals the start of redemptive history. The book of Genesis relates the beginnings of this redemptive history. In focusing on this single history, Genesis exhibits a unified structure.")])])]), _c('a', {
    attrs: {
      "id": "page_21"
    }
  }, [_c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-11"
    }
  }, [_c('em', [_vm._v("The Literary Structure of Individual Narratives")])])]), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_21"
    }
  }, [_vm._v("To understand a biblical narrative and to preach it in a narrative form, it is crucial to detect the plot line in the narrative. To discover the plot line, one should ask, What is the conflict in this story and how is it resolved? All narratives will have some of the following components: a setting for the story, some preliminary incidents, an incident that generates the conflict, buildup in the tension till it reaches a climax, the turn in the narrative to the beginning of a resolution, the full resolution, an outcome, and perhaps a conclusion. Tremper Longman has provided a helpful diagram of the typical elements in a single plot."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_50",
      "id": "fn-50"
    }
  }, [_vm._v("50")])]), _vm._v(" "), _c('p', {
    staticClass: "para-ceni"
  }, [_c('strong', [_vm._v("The Structure of Biblical Narrative")])]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/21.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("Hebrew narratives have various plot lines: a single plot which relates a single narrative conflict and resolution (e.g., Babel, Gen 11:1–9), a single plot with subplots or complications (e.g., the call of Abram, 11:27–12:9), a complex plot which relates conflict and resolution only to usher in a new conflict leading to another resolution (e.g., the Fall into sin, 2:4–3:24), an overarching plot in "), _c('a', {
    attrs: {
      "id": "page_22"
    }
  }, [_vm._v("which the initial conflict (e.g., God’s promise to make of Abram a great nation when his wife is barren, 11:30; 12:2) is not resolved until the birth of Isaac (21:1–7) and then only partially, and the Bible’s meta-narrative plot from Genesis 1 to Revelation 22 (see the plot line on ")]), _c('a', {
    attrs: {
      "href": "#/010_Chapter01#page_4"
    }
  }, [_vm._v("p. 4")]), _vm._v(" above).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Moreover, these various plot lines do not neatly follow one after the other but are intricately interwoven. A single plot may have embedded in it settings for overarching narratives. For example, the single plot of God calling Abram (11:27–12:9) has embedded in it God’s promise to Abram of “a great nation” (12:2). The conflict of this overarching narrative begins to be resolved only with the birth of Isaac (21:1–7), then Jacob, Jacob’s sons, and the full number of seventy Israelites going down to Egypt (46:27). The single plot of God calling Abram also contains God’s promise of land (12:7). The conflict of this overarching narrative begins to be resolved only when Abram is able to buy a burial plot for Sarah (23:1–20) but by the end of Genesis is still waiting for full resolution. God also promises Abram, “in you all the families of the earth shall be blessed” (12:3)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_51",
      "id": "fn-51"
    }
  }, [_vm._v("51")])]), _vm._v(" This overarching narrative conflict receives an initial resolution only when “all the world came to Joseph in Egypt to buy grain” (41:57) but is still waiting for full resolution. The following diagram shows the interweaving of single and overarching narratives that begin to be resolved in Genesis but reach beyond it for full resolution:")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/22.jpg"
    }
  })]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-12"
    }
  }, [_c('em', [_vm._v("The Genre of the Genesis Narratives")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Another important issue one faces in literary interpretation is the question of the "), _c('em', [_vm._v("kind")]), _vm._v(" of literature one is interpreting. Although this question is a literary question, for the Genesis narratives it is often informed by a judgment of their "), _c('a', {
    attrs: {
      "id": "page_23"
    }
  }, [_vm._v("historicity. Granted that these narratives are not the genre of modern historical narrative, what are they? A multitude of answers have been offered to this question, the three most prominent being legend, myth, and saga.")])]), _c('a', {
    attrs: {
      "id": "page_23"
    }
  }), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-9"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_23"
    }
  }, [_c('strong', [_vm._v("Legend")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Some have suggested that the Genesis narratives are legends. For example, George Coats states that the legend “employs a relatively static narration. The structure of the legend features recurring emphasis on some particular characteristic of the narrative’s hero.… The structure does not develop an arc of tension that moves from point of complication to point of resolution.… The goal of the legend is edification of its audience. Thus, the hero may serve as a model whose virtue can be duplicated by subsequent generations.”"), _c('sup')])]), _c('a', {
    attrs: {
      "href": "#/#fn_52",
      "id": "fn-52"
    }
  }, [_vm._v("52")]), _vm._v(" Coats identifies specifically Genesis 22 (Abraham) and 39–41 (Joseph) as legends. However, as we shall see, these narratives do exhibit the buildup of tension from conflict to resolution. It also seems far-fetched to suggest that Israel, with its penchant for historical foundations, would seek to base its history with God and its claim to the land of Canaan on mere legends.")]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-10"
    }
  }, [_c('strong', [_vm._v("Myth")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Others have suggested that the Genesis narratives are “myths.” But “myth” is a slippery term, witness the fact that scholars use at least nine different definitions of “myth.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_53",
      "id": "fn-53"
    }
  }, [_vm._v("53")])]), _vm._v(" According to McCartney and Clayton, “the common meaning of the term myth in popular parlance is ‘a fabulous and untrue story.’ ” This denotation, they say, makes the term “myth” totally inadequate for Genesis, for “biblical history is not myth, but a true story, told with theological purpose and vantage point. It may use the images and linguistic forms of its environment, but slipping in the term myth by redefinition really results in a reduction of the uniqueness of the biblical history.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_54",
      "id": "fn-54"
    }
  }, [_vm._v("54")])]), _vm._v(" Moreover, the Genesis narratives "), _c('em', [_vm._v("de")]), _vm._v("mythologize pagan mythologies."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_55",
      "id": "fn-55"
    }
  }, [_vm._v("55")])]), _vm._v(" Surely, the label of “myth” is inappropriate for narratives that demythologize pagan mythologies.")])]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-11"
    }
  }, [_c('strong', [_vm._v("Saga")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Hermann Gunkel first introduced the name “saga” for the Genesis narratives. “Saga,” a Norse term, has been defined as “a long, prose, traditional narrative having an episodic structure developed around stereotyped themes or objects.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_56",
      "id": "fn-56"
    }
  }, [_vm._v("56")])]), _vm._v(" The major unresolved issue with saga is whether and how much of the narrative is historical."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_57",
      "id": "fn-57"
    }
  }, [_vm._v("57")])]), _vm._v(" Although Donald Gowan bemoans the fact that it is "), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("a “vague” label, he asserts that “nevertheless the term saga can be used in a helpful way to denote a type of literature which is different in form, content, and intention from history-writing, so different that it ought to be preached in a distinctive way.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_58",
      "id": "fn-58"
    }
  }, [_vm._v("58")]), _vm._v(" By contrast, Dillard and Longman judge that labels such as saga, legend, fable, and myth “are obviously prejudicial to the historical intentionality of the book. They are … motivated more by modern interpreters’ unwillingness and inability to accept the reality of the world of Genesis than by a clear insight into the intention of the text.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_59",
      "id": "fn-59"
    }
  }, [_vm._v("59")])]), _vm._v(" Asking about the “intention of the text” may lead to a more solid footing than the vague and slippery labels above.")])]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-12"
    }
  }, [_c('strong', [_vm._v("Redemptive-Historical Narrative")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("In discussing the "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" structure of Genesis, we noted that the author has gone to great lengths to unify his composition by structuring the whole as ten sets of "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(". The author’s intention clearly is to link the nation of Israel via the patriarchs to the very beginning of history. “The frequent "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" formulae that structure the book … indicate a historical impulse to the book.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_60",
      "id": "fn-60"
    }
  }, [_vm._v("60")])]), _vm._v(" Bruce Waltke argues effectively that Genesis is some form of history writing: “The author of Genesis presents himself as a historian.… He gives an essentially coherent chronological succession of events, using the Hebrew narrative verb form. He validates his material as much as possible by locating his story in time and space (e.g., 2:10–14), tracing genealogies (e.g., 5:1–32), giving evidence of various sorts that validate his history (e.g., 11:9), and citing sources (5:1 [‘the written account’]).… The narrator’s evidence will not satisfy the demands of modern historiography, but it shows that he intended to write real history, not myth or saga or legend.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_61",
      "id": "fn-61"
    }
  }, [_vm._v("61")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("If it is clear that Genesis contains a form of history writing, it is also clear that it is a special form. This is no national history, not even tribal or family history. The author focuses on "), _c('em', [_vm._v("God’s")]), _vm._v(" history with the world and the patriarchs. Therefore we can call the genre of Genesis narratives redemptive-historical narrative."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_62",
      "id": "fn-62"
    }
  }, [_vm._v("62")])]), _vm._v(" We can define redemptive-historical narrative as narrative which recounts the history God makes in the world to restore his kingdom on earth and "), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }, [_vm._v("redeem his people. In contrast to legend, myth, and saga, the genre of redemptive-historical narrative not only acknowledges that this is a form of history writing but also connects these Genesis narratives to other biblical narratives which together, like frames in a film, constitute the meta-narrative from fallen creation (Gen 3) to new creation (Rev 22).")])]), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_25"
    }
  }, [_vm._v("Redemptive history is not above ordinary history; redemptive history unfolds in ordinary history."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_63",
      "id": "fn-63"
    }
  }, [_vm._v("63")]), _vm._v(" Yet the common historical-critical method is incompetent to evaluate redemptive history because it assumes that it can apply to biblical narratives the principles of analogy and correlation in a universe closed to God."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_64",
      "id": "fn-64"
    }
  }, [_vm._v("64")])]), _vm._v(" Because of this starting point, this method is inherently incapable of competently assessing redemptive history. For example, the historical-critical method may some day be able to confirm the probability that Jacob’s son Joseph rose to high power in Egypt, but it cannot confirm the biblical message that “God intended it for good, in order to preserve a numerous people” (Gen 50:20). To do justice to the genre of redemptive-historical narrative, one needs to utilize a holistic historical-critical method, that is, a method that presupposes an open universe where God can act in history either mediately or immediately."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_65",
      "id": "fn-65"
    }
  }, [_vm._v("65")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-7"
    }
  }, [_c('strong', [_c('em', [_vm._v("Issues in Historical Interpretation")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("With the discussion of the genre of the Genesis narratives we have already entered the field of historical interpretation. Here we will discuss three major issues: "), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_vm._v("first, the historicity of the Genesis narratives; second, their indispensable historical foundations; and third, their original readers.")])]), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-13"
    }
  }, [_c('em', [_vm._v("The Historicity of the Genesis Narratives")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The Genesis narratives are obviously not modern historiography. For one, they are not eyewitness accounts. If, as tradition has it, Moses was the original author, he lived at least six centuries after Abraham. Having led Israel out of Egypt, he wrote about Israel’s ancestors, pushing its origins back ever further: to its patriarchs, to the first nations, to the first humans, to the beginning of the world. We can describe the history-writing of Genesis as ancient, kerygmatic historiography.")])]), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-13"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_c('strong', [_vm._v("Ancient Historiography")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("The ancient author is clearly not confined by nineteenth-century standards of exactitude. He is unashamedly selective. “Only that which contributes to the story of God, i.e., to the theological intention of the text, is worthy of comment. Thus, whole centuries can be bypassed … or long chapters can be devoted to a relatively brief period of time.”"), _c('sup')])]), _c('a', {
    attrs: {
      "href": "#/#fn_66",
      "id": "fn-66"
    }
  }, [_vm._v("66")]), _vm._v(" For example, the author devotes thirteen long chapters to the life of Joseph while he simply skips over more than three centuries between Jacob’s death (Gen 50) and Moses’ birth (Exod 2). The author of Genesis selects some facts here and ignores others there; he details particulars here and summarizes others there; he arranges certain events in chronological order and then switches to a topical order (e.g., the reversal in Gen 10 and 11:1–9); he uses anachronism by pushing the distinction between clean and unclean animals back to Noah; he employs hyperbole to make his point when he reports that “"), _c('em', [_vm._v("all the world")]), _vm._v(" came to Joseph in Egypt to buy grain” (Gen 41:57); he uses the seven-day week known to Israel as a literary framework to proclaim that Israel’s God created all things; he places Enoch who “walked with God” seventh in the line of the seed of the woman to contrast him with the murderous Lamech, seventh in the line of the seed of the serpent; he uses round, symbolic numbers: ten sets of "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" from the creation of heaven and earth to Israel in Egypt, ten generations from Adam to Noah and ten generations from Shem to Abram, forty days and forty nights of rain causing the flood, seventy nations descended from Noah (Gen 10), seventy persons descended from Jacob (Gen 46:27). Even the ages listed for the patriarchs may be intended as symbolic numbers when one considers that Abraham’s age of 175 (25:7), Isaac’s of 180 (35:28), Jacob’s of 147 (47:28), and Joseph’s of 110 (50:26) form a refined symmetry which begins and ends with the perfect number seven:"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_67",
      "id": "fn-67"
    }
  }, [_vm._v("67")])])]), _vm._v(" "), _c('p', {
    staticClass: "para-lt"
  }, [_c('a', {
    attrs: {
      "id": "page_27"
    }
  }, [_vm._v("175 = 7 × 52")])]), _c('a', {
    attrs: {
      "id": "page_27"
    }
  }, [_c('p', {
    staticClass: "para-l"
  }, [_vm._v("180 = 5 × 62")]), _vm._v(" "), _c('p', {
    staticClass: "para-l"
  }, [_vm._v("147 = 3 × 72")]), _vm._v(" "), _c('p', {
    staticClass: "para-l"
  }, [_vm._v("110 = 1 × 52 + 62 + 72.")])]), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_27"
    }
  }, [_vm._v("Evidently the author of Genesis did not intend to give Israel exact information about the distant past, and the narratives should not be understood as such."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_68",
      "id": "fn-68"
    }
  }, [_vm._v("68")])]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-14"
    }
  }, [_c('strong', [_vm._v("Kerygmatic Historiography")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("It is better to understand Genesis as kerygmatic historiography. The author sketches in broad strokes the beginnings of God’s redemptive history in order to acquaint Israel with the God of their ancestors and his acts to preserve a people and to restore his kingdom on earth. For preachers and teachers this kerygmatic character is advantageous, for “the bare facts of history are usually capable of several different interpretations, and the inner meaning of the events, Yhwh’s purpose behind the occurrences, is not clear unless it is disclosed in words which Yhwh speaks to his chosen messengers.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_69",
      "id": "fn-69"
    }
  }, [_vm._v("69")])]), _vm._v(" The Genesis narratives may be described as sermons addressed to ancient Israel; they seek to impart God’s relevant message to Israel."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_70",
      "id": "fn-70"
    }
  }, [_vm._v("70")])]), _vm._v(" As such they can be used for relevant proclamation to the new Israel, the church, today.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Christian preachers should honor the intention of the author of Genesis, of course: they should not put questions to the text which the author did not intend to answer. For example, they should not ask Genesis about the age of the earth or the sequence in which things came to be. Instead of raising these modern questions, they should use Genesis to listen in on the relevant message that came to ancient Israel and apply that message to the church today.")]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-15"
    }
  }, [_c('strong', [_vm._v("The Sources of Genesis")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Since Genesis was written long after the recorded events, the author must have used ancient sources to compose his work."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_71",
      "id": "fn-71"
    }
  }, [_vm._v("71")])]), _vm._v(" Eugene Merrill observes, “The tradition is silent as to how Moses (or any author) "), _c('a', {
    attrs: {
      "id": "page_28"
    }
  }, [_vm._v("gained access to the events of that pre-Mosaic era, though perhaps terms such as "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" might suggest written texts.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_72",
      "id": "fn-72"
    }
  }, [_vm._v("72")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("As far as primeval history is concerned, Ken Mathews suggests that “Genesis appears to be following an ancient pattern for the way origins were told among the peoples of the Near East. The closest parallels are the Akkadian "), _c('em', [_vm._v("Atrahasis")]), _vm._v(" (1600 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C")]), _vm._v(".) and the Sumerian "), _c('em', [_vm._v("Eridu Genesis")]), _vm._v(" (1600 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C")]), _vm._v(".) which give an account of the period from creation to the great Flood. Other parallels to Genesis may be drawn from such myths as the Akkadian "), _c('em', [_vm._v("Enumah Elish")]), _vm._v(" and "), _c('em', [_vm._v("Epic of Gilgamesh")]), _vm._v(".” Mathews judges, “There is no evidence that outright borrowing occurred between the Hebrews and others; rather, there was general knowledge of early traditions shared by all antiquity.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_73",
      "id": "fn-73"
    }
  }, [_vm._v("73")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("These sources, their dates, and how they were used is a never-ending debate—a debate preachers may be inclined to sidestep. In seeking to understand the message of the text, however, awareness of underlying sources or parallel developments may be helpful."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_74",
      "id": "fn-74"
    }
  }, [_vm._v("74")])]), _vm._v(" For the way the biblical author uses, changes, or counters his sources may sharpen the particular message he wishes to bring to Israel. For example, all the ancient myths speak of a multitude of gods. In sharp contrast, Genesis begins: “In the beginning God created the heavens and the earth.” In the creation account "), _c('em', [_vm._v("Atrahasis")]), _vm._v(", the gods persuade the goddess Mami to “create a human being that he bear the yoke,” that is, the “forced labor” of the lesser gods."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_75",
      "id": "fn-75"
    }
  }, [_vm._v("75")])]), _vm._v(" By contrast, Genesis 1 proclaims that God created human beings not as slaves but as the crown of his creation, made in his image, his representatives, to take care of the earth. Again, “"), _c('em', [_vm._v("Atrahasis")]), _vm._v(" and the "), _c('em', [_vm._v("Eridu Genesis")]), _vm._v(" attribute the Flood to the last desperate attempts of the gods to rid themselves of the overpopulated, bothersome humans. The survival of their ‘Noah’ was unintended and only accepted by the gods after a compromise. The Bible, on the other hand, presents the population of the earth as good in the eyes of God; procreation is a blessing and will bring to pass God’s reign on earth through Adam’s lineage. More importantly, the Flood is God’s judgment against the encroaching world-wide practice of human sin.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_76",
      "id": "fn-76"
    }
  }, [_vm._v("76")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("As far as patriarchal history is concerned, though the author may not be as far removed from his subject matter as from primeval history, he is still at least six centuries "), _c('a', {
    attrs: {
      "id": "page_29"
    }
  }, [_vm._v("removed from Abraham. Where did he get his information? Allen Ross suggests that “besides the primeval traditions and genealogies brought from the East, the family traditions of the patriarchs would have been handed down from generation to generation. Joseph, and later Moses, would have had every facility for recording and preserving the traditions that the ancestors brought with them.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_77",
      "id": "fn-77"
    }
  }, [_vm._v("77")])]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-16"
    }
  }, [_c('strong', [_vm._v("The Reliability of Genesis")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Archeologists have discovered interesting parallels to places and customs described in Genesis. Victor Hamilton sums up some of the evidence: Tablets from Mari (19th century "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.")]), _vm._v(") “revealed place names and personal names (a) equivalents of which are found in the early chapters of Genesis …, and (b) most of these names were restricted to the Bronze Age.” Further, texts from Nuzi (15th century "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.")]), _vm._v(") showed the following parallels:")])]), _vm._v(" "), _c('p', {
    staticClass: "num-listt"
  }, [_c('span', {
    staticClass: "pad"
  }, [_vm._v("1."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("A marriage to a niece (11:29)")])])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_c('span', {
    staticClass: "pad"
  }, [_vm._v("2."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("A husband obtains the status of a brother by adopting his wife (12:1–20; 21:1–34; 26:1–35)")])])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_c('span', {
    staticClass: "pad"
  }, [_vm._v("3."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("A childless couple might adopt someone, even a servant, to take care of them; in the end this person would inherit their property. Any naturally born son, however, replaces the adoptee (15:2–3)")])])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_c('span', {
    staticClass: "pad"
  }, [_vm._v("4."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("A barren wife must provide her husband with a surrogate, normally the wife’s slave girl (16:1–2; 30:1–13)")])])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_c('span', {
    staticClass: "pad"
  }, [_vm._v("5."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("The status of the slave girl and her offspring is protected against the jealousy or whims of either wife or husband (21:9–14)")])])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_c('span', {
    staticClass: "pad"
  }, [_vm._v("6."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("A brother may adopt his sister in order to give her in marriage to someone else, providing she agrees (24:1–67)")])])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_c('span', {
    staticClass: "pad"
  }, [_vm._v("7."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("A birthright might be sold to another (25:29–34)")])])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_c('span', {
    staticClass: "pad"
  }, [_vm._v("8."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("A patriarchal blessing carries the weight of law and is not to be subjected to revision (27:35–37; 48:8–22)")])])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_c('span', {
    staticClass: "pad"
  }, [_vm._v("9."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("A couple might adopt a son-in-law as their own son (30:1–2)")])])]), _vm._v(" "), _c('p', {
    staticClass: "num-list1"
  }, [_c('span', {
    staticClass: "pad"
  }, [_vm._v("10."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Possession of the household gods was seen as legal title to an inheritance (31:34)"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_78",
      "id": "fn-78"
    }
  }, [_vm._v("78")])])])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("John Sailhamer judges, “However Moses may have obtained his information, one thing is certain: the Pentateuch depicts accurately the age and historical period of the patriarchs.… Many of the historical details and customs in the lives of the patriarchs are now known to us from contemporary documents.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_79",
      "id": "fn-79"
    }
  }, [_vm._v("79")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_30"
    }
  }, [_vm._v("Still, it is well to remember that the authority of the preacher’s message is not dependent on nineteenth-century concepts of historical accuracy. As Clyde Francisco observes, “The truths of Genesis 1–11 are not to be found so much in the exactness with which the original event has been preserved as in the witness to that event. Witnesses in court give clues to the judge concerning the actual event; they cannot reproduce it.… Even so, the biblical accounts, regardless of their condition in transmission, give authentic witness to the redemptive acts of God which are both before and beyond their words.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_80",
      "id": "fn-80"
    }
  }, [_vm._v("80")])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-14"
    }
  }, [_c('em', [_vm._v("Indispensable Historical Foundations")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Although fiction can surely proclaim truth (e.g., Jesus’ parables), preachers cannot proclaim redemptive-historical truth on the basis of fiction. When preachers proclaim with Genesis that God acted in world and human history, their message has integrity only when they themselves assume that God acted in history.")]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-17"
    }
  }, [_c('strong', [_vm._v("Creating a Narrative World")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("In the section on literary interpretation above, we saw how the author/compiler of Genesis was able to present a compelling narrative linking the history of Israel as a nation to the patriarchs and ultimately to the creation of this world. With the recent paradigm shift in biblical scholarship from historical criticism to the new literary criticism and especially narrative criticism, biblical scholars have opened up wonderful vistas for preachers of biblical narrative. Narratives are now carefully analyzed according to their setting, characters, dialogue, plot, point of view, narrator and narratee, implied author and implied reader, and real author and real reader."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_81",
      "id": "fn-81"
    }
  }, [_vm._v("81")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Unfortunately, in the quest for literary meaning and in reaction to the onslaught of secular historical-criticism, biblical scholars now frequently ignore the historical referent."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_82",
      "id": "fn-82"
    }
  }, [_vm._v("82")])]), _vm._v(" They simply wish to “bracket out” the question of historicity. For example, Mark Ellingsen writes, “The strength of the biblical narrative approach for preaching on the strange biblical accounts is precisely that formal "), _c('a', {
    attrs: {
      "id": "page_31"
    }
  }, [_vm._v("historical claims are not made on their behalf.” “The preacher’s job is simply to report the biblical accounts, not to insist that the strange stories happened.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_83",
      "id": "fn-83"
    }
  }, [_vm._v("83")]), _vm._v(" Grant Osborne, by contrast, insists, “If cut off from historical and referential meaning, they become arbitrary and subjective. Therefore, any proper methodology must blend the two (literary and historical) in such a way that they modify one another, magnifying the strengths and avoiding the weaknesses of each.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_84",
      "id": "fn-84"
    }
  }, [_vm._v("84")])])]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-18"
    }
  }, [_c('strong', [_vm._v("The Genesis Narrative World Depicts the Real World")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("John Goldingay observes that “large tracts of the Old Testament such as Genesis are put in the form of narrative about the factual past. Israel was capable of producing fictional parable and present testimony, but in the patriarchal stories she did not: she makes a point of telling a story about the factual past, and refers to it (in a passage such as Isaiah 51) in such a way as to make it rather clear that she understands the story to be fundamentally factual.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_85",
      "id": "fn-85"
    }
  }, [_vm._v("85")])]), _vm._v(" In other words, the narrative world of Genesis depicts the real world. In opposition to Frei and others who suggest that biblical narratives are only “history-like,” Sailhamer maintains, “It is not enough to say that the biblical narratives are only ‘history-like’ and to relegate them to the level of ‘realistic narrative [Frei].’ … The authors of the biblical narratives give every indication of intending their works to be taken as history rather than fiction.… There is reasonable evidence that the history recorded in these narratives corresponds to the events themselves.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_86",
      "id": "fn-86"
    }
  }, [_vm._v("86")])]), _vm._v(" Sailhamer’s diagram will help clarify the relationships between the reader, redemptive-historical narrative, the narrative world, and the real world:"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_87",
      "id": "fn-87"
    }
  }, [_vm._v("87")])])])]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/31.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-19"
    }
  }, [_c('strong', [_vm._v("The Necessity of Historical Foundations")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Redemptive-historical narrative requires historical foundations for its message to stand."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_88",
      "id": "fn-88"
    }
  }, [_vm._v("88")])]), _vm._v(" Not all narratives, of "), _c('a', {
    attrs: {
      "id": "page_32"
    }
  }, [_vm._v("course, require historical foundations. Parables and allegories carry their messages without historical foundations. But for redemptive-historical narratives, the lack of historical foundations is fatal, for the factuality that God acted in history is part and parcel of their message. For example, if the message of Genesis 1 is that God created this world good, then preachers cannot preach this text with integrity if they adhere to the theory of naturalistic evolutionism. Questions of "), _c('em', [_vm._v("how")]), _vm._v(" God created and "), _c('em', [_vm._v("when")]), _vm._v(" he created and the "), _c('em', [_vm._v("order")]), _vm._v(" in which he created are secondary. But doubts about God being the Creator of this universe or about God creating this world good (as in the hypothesis of gnosticism) undermine and negate the message of Genesis 1. Or, to take another example, the message that God judged human violence with a great flood (Gen 6–9) requires an actual, historical flood. The question whether it was a universal flood or a local flood is secondary. We may never find indisputable proof of these historical events at the dawn of human history, but “absence of evidence is "), _c('em', [_vm._v("not")]), _vm._v(" evidence of absence.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_89",
      "id": "fn-89"
    }
  }, [_vm._v("89")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Or, to give a few more examples, if the author’s message of Genesis 12 is that God calls Abram from Haran to the land of Canaan and Abram is a fictional character, the message is groundless. If the message of Genesis 28 is that God promises the land of Canaan to Jacob and his descendants, and Jacob is a fictional character, the message evaporates. If the message of Genesis 39 is that God was with Joseph in his suffering, and Joseph is not a historical figure, the message melts away. One should not use the pulpit, of course, to try to “prove” the historical nature of Genesis. It is sufficient for modern preachers and congregations to hear these narratives with the same assumption as that of the original author and readers, and that is the assumption of general historical reliability."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_90",
      "id": "fn-90"
    }
  }, [_vm._v("90")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-15"
    }
  }, [_c('em', [_vm._v("The Original Readers of Genesis")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("A frequently overlooked dimension of historical interpretation is the historical situation of the original readers of Genesis. Knowing the situation of the first "), _c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_vm._v("recipients of Genesis is of crucial importance for catching the relevance of the Genesis narratives. At this level one raises questions such as: Who wrote this book? To whom? When? Where? And why?")])]), _c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Let’s look at a simple example to illustrate the importance of historical interpretation at this level. Suppose the selected preaching-text is from 1 Corinthians. The preacher asks: Who wrote this text? To whom? When? Where? And why? Biblical scholars respond with clear answers: Paul wrote this letter to a struggling church in the sinful harbor city of Corinth, around "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("A.D.")]), _vm._v(" 55, because Paul received word of immorality and other problems in this church. These answers of the date and of the intended readers/hearers and their needs allows preachers to hone in on the goals Paul had in mind in writing this letter. For example, some of Paul’s goals were to encourage the Corinthian Christians to live a distinctively Christian life in the midst of their sinful culture, to warn them against divisions in the church, to motivate them to walk in love, and to convince them of the reality of Jesus’ resurrection. Inasmuch as these Corinthian problems still exist in the church today, preachers can readily and relevantly transfer to their congregations Paul’s various messages to the church in Corinth.")])]), _c('p', {
    staticClass: "indent-para"
  }, [_c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_vm._v("But who was the author of Genesis? The answer to this question is difficult because Genesis is an anonymous work and gives evidence of later “updates.” There is no doubt, however, that “early Jewish and Christian tradition … is virtually unanimous in ascribing Genesis through Deuteronomy to him [Moses].”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_91",
      "id": "fn-91"
    }
  }, [_vm._v("91")]), _vm._v(" Modern source criticism, because of its late dating of the Genesis sources, abandoned this traditional view. Still, there are good reasons to continue to assume that Moses was the author/compiler of at least the original core of the Pentateuch—what Waltke calls “Ur-Genesis.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_92",
      "id": "fn-92"
    }
  }, [_vm._v("92")])]), _vm._v(" Exodus 2 relates that Moses was adopted by Pharaoh’s daughter—a position that would have given him access to a superior education."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_93",
      "id": "fn-93"
    }
  }, [_vm._v("93")])]), _vm._v(" Moreover, the Pentateuch recounts that the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" at various times instructed Moses to write certain events in a book for posterity: “Write this as a reminder in a book” (Exod 17:14), and Moses did so. Again, “Moses wrote down their starting points, stage by stage, by command of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("” (Num 33:2). The L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" also instructed Moses to write down the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v("’s words."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_94",
      "id": "fn-94"
    }
  }, [_vm._v("94")])]), _vm._v(" By the time of Joshua, as Israel was about to cross the Jordan, the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" warned Israel to be “careful to act in accordance with all "), _c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_vm._v("the law that my servant "), _c('em', [_vm._v("Moses")]), _vm._v(" commanded you.… "), _c('em', [_vm._v("This book of the law")]), _vm._v(" shall not depart out of your mouth … (Josh 1:7–8)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_95",
      "id": "fn-95"
    }
  }, [_vm._v("95")]), _vm._v(" Later, “the exilic and postexilic writers refer to the Pentateuch as the Law, the Law of Moses, the Book of Moses, and the Book of the Law of Moses.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_96",
      "id": "fn-96"
    }
  }, [_vm._v("96")])]), _vm._v(" In the New Testament, Jesus and his disciples, as well as his opponents, ascribe the first books of the Bible to Moses."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_97",
      "id": "fn-97"
    }
  }, [_vm._v("97")])]), _vm._v(" Bruce Waltke concludes, “The founder of Israel [Moses] is the most probable person to transpose its national repository of ancient traditions into a coherent history in order to define the nation and its mission.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_98",
      "id": "fn-98"
    }
  }, [_vm._v("98")])]), _vm._v(" But John Sailhamer cautions, “We should not lose sight of the fact that the Pentateuch itself comes to us as an anonymous work and was apparently intended to be read as such.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_99",
      "id": "fn-99"
    }
  }, [_vm._v("99")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("For preaching purposes, however, the important question to answer is not so much, Who wrote the text? but to whom? when? where? and especially, why? These are the questions that get at the questions "), _c('em', [_vm._v("behind")]), _vm._v(" the text, the problems Israel encountered to which the text may be a response. Where was Israel when they received this word, and what were their needs? Was the author addressing his message to a terrorized Israel enslaved in Egypt, or to a fearful Israel about to enter Canaan, or to a confident and proud Israel during the heady times of David and Solomon, or to a crushed and confounded Israel (Judah) in exile? Unfortunately, biblical scholars have no clear answers to these questions, and that makes interpreting and preaching Genesis much more complicated than preaching 1 Corinthians."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_100",
      "id": "fn-100"
    }
  }, [_vm._v("100")])]), _vm._v(" Scholars have suggested several different backgrounds for Genesis.")]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-20"
    }
  }, [_c('strong', [_vm._v("Israel about to Enter the Promised Land")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Some have suggested that Moses wrote Genesis while Israel was enslaved in Egypt,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_101",
      "id": "fn-101"
    }
  }, [_vm._v("101")])]), _vm._v(" but that is not a likely setting. Rather, evidence suggests that Genesis was written "), _c('em', [_vm._v("after")]), _vm._v(" Israel received God’s laws at Sinai, for specific Sinaitic laws have made their way into Genesis. For example, Israel’s week of six days of work and one day of rest are reflected "), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }, [_vm._v("in the creation account. Further, God’s “ten words” (Exod 34:28) of the law are reflected in his ten words in creating the world (10 × “God said,” Gen 1:3–29)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_102",
      "id": "fn-102"
    }
  }, [_vm._v("102")]), _vm._v(" “Noah distinguishes between clean and unclean animals, works on a seven-day cycle and presumably keeps the Sabbath.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_103",
      "id": "fn-103"
    }
  }, [_vm._v("103")])]), _vm._v(" Later the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" says to Isaac that “Abraham obeyed my voice and kept my charge, my commandments, my statutes, and my laws” (Gen 26:5). These are indications that Genesis was written to Israel "), _c('em', [_vm._v("after")]), _vm._v(" God gave his law at Sinai.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Moreover, as John Sailhamer observes, “It becomes clear as one reads through the second half of the Pentateuch that it was not written primarily to the generation that came out of Egypt. Its readership was specifically the generation of Israelites that was about to go into the Promised Land. All the events of the Exodus and the wilderness journey as well as the giving of the Law at Mount Sinai were cast as something that happened in the past.… The focus of the writer was on the future, the next generation.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_104",
      "id": "fn-104"
    }
  }, [_vm._v("104")])]), _vm._v(" Joseph’s final words in Genesis underscore this focus on the future: “God will surely come to you, and bring you up out of this land to the land that he swore to Abraham, to Isaac, and to Jacob” (Gen 50:24).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Accordingly, we should probably see the original addressees of the original book of Genesis as Israel in Moab, soon to enter the land of Canaan."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_105",
      "id": "fn-105"
    }
  }, [_vm._v("105")])]), _vm._v(" Israel had tried to enter the land earlier but failed miserably because of their great fear: “Our kindred [the spies] have made our hearts melt by reporting, ‘The people are stronger and taller than we; the cities are large and fortified up to the heaven!’ ” (Deut 1:28)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_106",
      "id": "fn-106"
    }
  }, [_vm._v("106")])]), _vm._v(" God punished their lack of faith with a forty-year desert journey marked with graves: everyone in that generation died in the desert, except Moses, Caleb, and Joshua. Now Israel still fears to enter the land, as we see in Joshua 1 with its repeated command, “Be strong and courageous” (vv 6 and 7), which is repeated for good measure in verse 9 with antithetic parallelism, “Be strong and courageous; do not be frightened or dismayed.” Moses could well be focusing the Genesis narratives on Israel’s fresh memories of that disastrous desert journey and their present fear of entering the Promised Land. As they are caught between a rock and a hard place, he reminds them that their L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(" is the great Provider for his people, that nothing is impossible for him, that he promised this land repeatedly to their fathers Abraham, Isaac, and Jacob, that he is faithful in fulfilling his promises, and that his presence with them "), _c('a', {
    attrs: {
      "id": "page_36"
    }
  }, [_vm._v("(as with Jacob and Joseph) means protection and life. Therefore, “Do not be frightened or dismayed!”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_107",
      "id": "fn-107"
    }
  }, [_vm._v("107")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Moreover, before entering Canaan, Israel had to understand “who they were, how they originated, and what purpose they were to serve as the covenant people of Yahweh. This required a sketch of their history to that point, first as a people delivered from Egyptian bondage to become at Sinai a covenant nation, and second as descendants of a common father who found themselves in Egypt in the first place. What was required next was a narrative linkage between themselves and those ancestors of ancient times.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_108",
      "id": "fn-108"
    }
  }, [_vm._v("108")])]), _vm._v(" These are some of the reasons why Moses would have written the original book of Genesis to Israel in Moab.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("But just as there are later additions to “The proverbs of Solomon son of David, king of Israel” (Prov 1:1; see Prov 30:1 and 31:1) as well as later additions to the book of Isaiah of Jerusalem (Isa 1:1; see Isa 40–66), so there may well be later additions to Moses’ account and revisions to bring into focus its relevance for later Israel. Israel in Moab, therefore, is not the only historical background of Genesis we need to consider.")]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-21"
    }
  }, [_c('strong', [_vm._v("Israel in the Promised Land")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Some post-Mosaic additions (glosses) in Genesis suggest that a later editor clarified these narratives for Israel in Canaan, probably in the time of David and Solomon. The phrase, “At that time the Canaanites were in the land” (Gen 12:6b; 13:7b), seems to point to an editor living in Israel when there were no Canaanites in the land. Genesis 14 contains at least five later geographical updates (see vv 2, 3, 7, 8, and 17). Moreover, v 14 states that “Abram pursued Lot’s captors as far as Dan, yet the place did not receive this name until the Danites captured it following the Conquest (Josh. 19:47; Judg. 18:29).”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_109",
      "id": "fn-109"
    }
  }, [_vm._v("109")])]), _vm._v(" The story of Jacob’s wrestling at Peniel now ends with the later comment, “Therefore to this day the Israelites do not eat the thigh muscle that is on the hip socket, because he struck Jacob on the hip socket at the thigh muscle” (Gen 32:32). “Genesis 36:31, at the beginning of a list of Edomite kings, states that they all ruled ‘before any king reigned over the Israelites.’ Obviously the writer’s viewpoint can only be sometime after Saul.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_110",
      "id": "fn-110"
    }
  }, [_vm._v("110")])]), _vm._v(" These post-Mosaic additions do not necessarily change the original relevance of these messages; they seek only "), _c('a', {
    attrs: {
      "id": "page_37"
    }
  }, [_vm._v("to clarify these messages for later Israel under David and Solomon when God’s promises to the patriarchs come to major fulfillments."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_111",
      "id": "fn-111"
    }
  }, [_vm._v("111")])])]), _vm._v(" "), _c('p', {
    staticClass: "sec4",
    attrs: {
      "id": "sec4-22"
    }
  }, [_c('strong', [_vm._v("Israel in Exile")]), _vm._v(" "), _c('span', {
    staticClass: "pad"
  }, [_vm._v("In time, Genesis became part of a single narrative that runs from Genesis through 2 Kings—from creation to exile. This unified collection of books probably received its final form around the time of Israel’s (Judah’s) exile. John Goldingay asserts, “Since they ultimately came to belong to a work that culminates in the exile, it is appropriate to interpret them against this historical context.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_112",
      "id": "fn-112"
    }
  }, [_vm._v("112")])]), _vm._v(" This lengthy narrative doubles back on itself. “The narrative that begins in Genesis by creating order from formlessness and then by taking Abram out of Ur of the Chaldeans [a first-millennium term used in Genesis 11:28 and 31] into the Promised Land ends in 2 Kings with chaos triumphant and Israel back in the power of the Chaldeans [2 Kings 25].”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_113",
      "id": "fn-113"
    }
  }, [_vm._v("113")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Since Genesis through 2 Kings is a single narrative in the final canonical form, Israel’s exile from the Promised Land is another major historical horizon in which we can hear the relevance of the Genesis narratives. Israel (Judah) is in exile, driven out of the Promised Land, banished from God’s presence, the covenant seemingly in tatters. The nation has been overrun by the mighty Babylonian armies supported by their powerful gods. How would Israel in exile hear the Genesis narratives? How would they hear the creation story of their God, just by speaking, bringing order out of chaos? How would they hear the story of their God creating the pagan gods Sun, Moon, and Stars only on the fourth day? How would they hear the flood story of a God who punished sin with death, yet in his grace saved a remnant? How would they hear the story of their father Abraham migrating from Ur of the "), _c('em', [_vm._v("Chaldeans")]), _vm._v(" (Gen 11:31), responding to God’s call to go to a land and being promised that land “forever” (13:15)? How would they hear the story of God’s promise to father Jacob before leaving the Promised Land for Haran: “Know that I am with you and will keep you wherever you go, and will bring you back to this land” (28:15)? Read against the background of the exile, the Genesis narratives begin to throb again with relevance. They can speak with renewed relevance because the needs of Israel in exile are very similar to the needs of Israel in Moab.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Today, of course, the Genesis narratives are part of an even larger literary unit that encompasses the whole Old Testament as well as the New Testament "), _c('a', {
    attrs: {
      "id": "page_38"
    }
  }, [_vm._v("and runs from creation (Gen 1) to the new creation (Rev 22). This metanarrative gives Christian preachers a final window for discerning the relevance of these narratives for the church today. The challenge for preachers, therefore, is to hear the message of each Genesis narrative first as Israel would have heard it against the backdrop of the needs in these various historical situations and to sense the inherent relevance of these narratives for Israel. The second challenge for preachers is to hear the theme and goal of each narrative in the light of the good news of Jesus Christ in the New Testament and to prepare their sermons against the backdrop of contemporary needs similar to those that Israel faced.")])]), _c('a', {
    attrs: {
      "id": "page_38"
    }
  }, [_c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-4"
    }
  }, [_c('strong', [_vm._v("Issues in Preaching the Genesis Narratives")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Before launching into our expository essays, we need to touch on a few more practical issues: preaching a sermon series on Genesis; how to handle lengthy narratives; the reasons for formulating the theme and goal of narratives; the application of narratives; the narrative sermon form; and oral style.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-8"
    }
  }, [_c('strong', [_c('em', [_vm._v("A Sermon Series on Genesis")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Like links in a chain, the Genesis narratives are connected to the foregoing and the following narratives. As such they are ideally suited for a sermon series. But preachers must be cautious not to extend the series so long that people start losing interest. Variety holds interest. I suggest, therefore, that one plan a series of five or six sermons on Genesis, then switch to different topics for a few months before returning to another series of sermons on Genesis. The book of Genesis breaks up into natural divisions for a number of such short series: five or six sermons on Israel’s prehistory (Gen 1:1–11:26), five or six on the Abraham cycle (11:27–25:11), five or six on the Jacob cycle (25:19–35:29), and five or six sermons on the Joseph narratives (37:2–50:26).")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-9"
    }
  }, [_c('strong', [_c('em', [_vm._v("Lengthy Narratives")])])])]), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_38"
    }
  }, [_vm._v("Preachers are frequently tempted to preach on a catchy phrase in a narrative or on an intriguing character. But this procedure fails to do justice to the inspired writer, for he does not proclaim his message in a single phrase or verse or character but in literary units. The smallest literary unit in the narrative genre is a scene, but usually single scenes don’t tell the whole story. It is normally best, ")]), _c('a', {
    attrs: {
      "id": "page_39"
    }
  }, [_vm._v("therefore, to select as one’s preaching-text a single narrative. The practical problem is that some of these narratives in Genesis are quite long. For example, the flood narrative runs from Genesis 6:9 to 9:17—more than three chapters. Do we have time in the worship service to read such a lengthy narrative before the sermon? Even if we do, how do we keep people listening? The first requirement is good interpretive reading. Preachers can do this themselves or they can ask excellent readers from the congregation. They can also assign three or more readers: a narrator, and two readers for the dialogue of the characters (usually two per scene). If the narrative is too long to read because of much repetition in Hebrew style, one can perhaps skip over some of the repetition, as long as the main story line is read. Or one can consider incorporating some of the reading into various parts of the sermon.")])]), _c('a', {
    attrs: {
      "id": "page_39"
    }
  }, [_c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-10"
    }
  }, [_c('strong', [_c('em', [_vm._v("The Theme and Goal of Narratives")])])])]), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_39"
    }
  }, [_vm._v("Some homileticians have objected to turning a dynamic biblical plot into a static theme for preaching purposes."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_114",
      "id": "fn-114"
    }
  }, [_vm._v("114")]), _vm._v(" But a dynamic plot and a static theme are not polar opposites. The relationship between plot and theme has been described as “two sides of the same formal principle with plot being theme in movement and theme being plot at a standstill.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_115",
      "id": "fn-115"
    }
  }, [_vm._v("115")])]), _vm._v(" True, the theme is an abstraction of the dynamic narrative. But the preacher needs that theme to focus the sermon so that the congregation gets the author’s point of telling the narrative. The issue of discovering the theme of the narrative is ultimately the issue of seeking to do justice to the message intended by the inspired author. The sermon itself should turn that static theme back into a dynamic narrative.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The goal, or aim, of the narrative raises the question why the author told Israel this particular story: what needs did he address? what response(s) did he seek? what was the relevance of this narrative for Israel? The goal must be determined on the basis of the message (theme)"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_116",
      "id": "fn-116"
    }
  }, [_vm._v("116")])]), _vm._v(" and the questions behind the text. When preachers observe a similar need in their congregations, they can retell the narrative with a goal similar to that of the biblical writer. The issue of discovering the goal of the narrative is ultimately the issue of the relevance of the sermon.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-11"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_c('strong', [_c('em', [_vm._v("The Application of Narratives")])])])]), _c('a', {
    attrs: {
      "id": "page_40"
    }
  }), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_vm._v("The goal of the sermon sets the stage for the application of the sermon. With inductive sermon development, the point (theme) of the narrative for Israel will not be disclosed until the narrative has been retold. This means that narrative sermons will usually have end application rather than continuous application. End application has much to commend it. First, the sermon can better keep the tension of the narrative and keep people "), _c('em', [_vm._v("in")]), _vm._v(" the story. Second, in saving the application for the end of the sermon, the application can be more concentrated and powerful than continuous application. And third, people will remember the application better because it is the final part of the sermon. To establish relevance, preachers should not feel obliged to sprinkle the sermon with practical asides (moralisms), for such comments, though practical, detract from the real relevance of the sermon. Current applications should be extensions of the original relevance of the narrative for Israel."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_117",
      "id": "fn-117"
    }
  }, [_vm._v("117")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In the following expository essays, I will try to show the direction for current application, but, not knowing the situation in the local church, I cannot go into detail. Preachers using this book for preparing sermons should expand the applications with personal observations and powerful illustrations to meet the needs of their local church.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-12"
    }
  }, [_c('strong', [_c('em', [_vm._v("The Narrative Sermon Form")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Narrative texts are best preached in the narrative sermon form."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_118",
      "id": "fn-118"
    }
  }, [_vm._v("118")])]), _vm._v(" This form not only allows preachers to follow in their sermon the form of the text (the plot line) but it also allows their hearers to be holistically engaged in the story (emotions as well as intellect). In preaching Old Testament narratives, however, preachers cannot simply retell the Old Testament story. For modern hearers, preachers will have to suspend the story from time to time to explain the historical background and cultural customs that were obvious to ancient Israel. Moreover, they will have to retell the narrative in such a way that its message for "), _c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_vm._v("Israel becomes evident to contemporary hearers. Furthermore, the message to Israel needs to be channeled via the New Testament and Christ to the church today. Therefore contemporary preachers cannot use a pure narrative form but will have to use a hybrid form.")])]), _c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Moving from the Old Testament to the New to the church today does not mean that all sermon forms will have the predictable movement:")]), _vm._v(" "), _c('p', {
    staticClass: "num-listt"
  }, [_vm._v("1."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Message for Israel;")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_vm._v("2."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Message in the context of the New Testament;")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_vm._v("3."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Application to the church today.")])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("These are the standard hermeneutical moves in one’s study, but to repeat these moves from the pulpit Sunday after Sunday might prove boring for the congregation. For variety, as well as for other reasons, one can start with a need in the church today, then flash back to the Old Testament, the New, and back to the church. Or one may be able to start in the New Testament, then flash back to the Old, then to the church today.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-13"
    }
  }, [_c('strong', [_c('em', [_vm._v("The Narrative Sermon’s Oral Style")])])])]), _c('p', {
    staticClass: "para"
  }, [_c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_vm._v("Many narrative sermons fail because they are delivered in written style. The narrative sermon, more than any other sermon form, ought to be written and delivered in a style that hearers can immediately absorb. That requirement calls for an oral style. Some of the characteristics of oral style are:"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#fn_119",
      "id": "fn-119"
    }
  }, [_vm._v("119")])]), _vm._v(" "), _c('p', {
    staticClass: "num-listt"
  }, [_vm._v("1."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Short sentences—mostly principal clauses, few relative clauses.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_vm._v("2."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Short, familiar words.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_vm._v("3."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Vivid, picture words—language that helps us to see the action.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_vm._v("4."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Strong nouns and verbs—words that enable us to see the action without complicating adjectives and adverbs.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_vm._v("5."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Specific, concrete language instead of general or abstract.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_vm._v("6."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("The active voice instead of the passive.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_vm._v("7."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Narration in the present tense instead of the past tense."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#fn_120",
      "id": "fn-120"
    }
  }, [_vm._v("120")])])])]), _vm._v(" "), _c('p', {
    staticClass: "num-list"
  }, [_c('a', {
    attrs: {
      "id": "page_42"
    }
  }, [_vm._v("8."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Verse reference "), _c('em', [_vm._v("before")]), _vm._v(" quoting a verse so the hearers can read along.")])])]), _c('a', {
    attrs: {
      "id": "page_42"
    }
  }, [_c('p', {
    staticClass: "num-list"
  }, [_vm._v("9."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Direct quotation of the dialogue of characters instead of indirect.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list1"
  }, [_vm._v("10."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Use of the indicative mood instead of imperative.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list1"
  }, [_vm._v("11."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Use of questions to involve people.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list1"
  }, [_vm._v("12."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Use of gender-inclusive language without calling attention to it.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list1"
  }, [_vm._v("13."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Use of the first person plural “we” rather than the second person “you.”")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list1"
  }, [_vm._v("14."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Verbal punctuation with words like “and,” “well,” “now,” “by the way.”")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list1"
  }, [_vm._v("15."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Important words at the end or beginning of clauses and sentences.")])]), _vm._v(" "), _c('p', {
    staticClass: "num-list1"
  }, [_vm._v("16."), _c('span', {
    staticClass: "pad"
  }, [_vm._v("Use of repetition and parallelism.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("In the following expository essays I seek to model the oral style as much as is possible in essays, highlight the theme and goal of each narrative, suggest ways of preaching Christ, and offer sermon expositions for each narrative. My intent is not that preachers incorporate all this material in their sermons, which would merely lead to information overload on the part of the hearers. Rather, my intent is that preachers would prudently select from the expositions as well as the ways of preaching Christ in order to produce sermons that are focused on the theme, biblical, relevant, and that communicate well in this postmodern age.")]), _vm._v(" "), _c('p', {
    staticClass: "image-fn"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/foot.jpg"
    }
  })])]), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "id": "page_42"
    }
  }), _c('a', {
    attrs: {
      "href": "#/#fn-1",
      "id": "fn_1"
    }
  }, [_vm._v("1")]), _vm._v(". See my "), _c('em', [_vm._v("Preaching Christ from the Old Testament")]), _vm._v(", 2–3.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-2",
      "id": "fn_2"
    }
  }, [_vm._v("2")]), _vm._v(". Ibid., 10.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-3",
      "id": "fn_3"
    }
  }, [_vm._v("3")]), _vm._v(". Ibid., 228–30.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-4",
      "id": "fn_4"
    }
  }, [_vm._v("4")]), _vm._v(". Ibid., 230–32.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-5",
      "id": "fn_5"
    }
  }, [_vm._v("5")]), _vm._v(". Goldsworthy, "), _c('em', [_vm._v("Preaching the Whole Bible as Christian Scripture")]), _vm._v(", 150.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-6",
      "id": "fn_6"
    }
  }, [_vm._v("6")]), _vm._v(". See my "), _c('em', [_vm._v("Preaching Christ from the Old Testament")]), _vm._v(", 70–109.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-7",
      "id": "fn_7"
    }
  }, [_vm._v("7")]), _vm._v(". See ibid., 234–77.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-8",
      "id": "fn_8"
    }
  }, [_vm._v("8")]), _vm._v(". See Oscar Cullmann, "), _c('em', [_vm._v("Christ and Time: The Primitive Christian Conception of Time and History")]), _vm._v(" (trans. Floyd V. Filson; rev. ed.; Philadelphia: Westminster, 1962), 178.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-9"
    }
  }, [_vm._v("9")]), _vm._v(". God declaring enmity between the serpent and the woman in Gen 3:15 is a turning point, but the fallout from the Fall into sin keeps spreading and going from bad to worse.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-10",
      "id": "fn_10"
    }
  }, [_vm._v("10")]), _vm._v(". For a quick overview of the use of typology by New Testament authors, see O. Palmer Robertson, "), _c('em', [_vm._v("The Israel of God")]), _vm._v(", 4–6. For a more elaborate presentation focusing on Matthew, see David Holwerda, "), _c('em', [_vm._v("Jesus and Israel")]), _vm._v(", 31–58.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-11",
      "id": "fn_11"
    }
  }, [_vm._v("11")]), _vm._v(". Compare the overlap between the disciplines of the history of redemption and the history of revelation. Progression in redemptive history generally involves progression in God’s revelation.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-12",
      "id": "fn_12"
    }
  }, [_vm._v("12")]), _vm._v(". See the New Testament references used above to support the ways of redemptive-historical progression, promise-fulfillment, typology, analogy, and longitudinal themes.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-13",
      "id": "fn_13"
    }
  }, [_vm._v("13")]), _vm._v(". Albert M. Wolters, "), _c('em', [_vm._v("Creation Regained: Biblical Basics for a Reformational Worldview")]), _vm._v(" (Grand Rapids: Eerdmans, 1985), 2.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-14",
      "id": "fn_14"
    }
  }, [_vm._v("14")]), _vm._v(". Mathews, "), _c('em', [_vm._v("Genesis 1–11:26")]), _vm._v(", 22.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-15",
      "id": "fn_15"
    }
  }, [_vm._v("15")]), _vm._v(". A favorite saying of Jack Roeda, my colleague in teaching first-year preaching at Calvin Theological Seminary. For the significance of the biblical worldview for Christian living in every area, see Nancy R. Pearcey, "), _c('em', [_vm._v("Total Truth: Liberating Christianity from Its Cultural Captivity")]), _vm._v(" (Wheaton, IL: Crossway, 2004).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-16",
      "id": "fn_16"
    }
  }, [_vm._v("16")]), _vm._v(". For reasons for the lack of preaching from the Old Testament in general, see my "), _c('em', [_vm._v("Preaching Christ from the Old Testament")]), _vm._v(", pp. 16–25.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-17",
      "id": "fn_17"
    }
  }, [_vm._v("17")]), _vm._v(". Throntveit, “Preaching from the Book of Genesis,” "), _c('em', [_vm._v("WW")]), _vm._v(" 14/2 (1994) 212.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-18",
      "id": "fn_18"
    }
  }, [_vm._v("18")]), _vm._v(". See, e.g., William Robinson, “Jesus Christ Is Jehovah,” "), _c('em', [_vm._v("EvQ")]), _vm._v(" 5 (1933) 145; T. W. Calloway, "), _c('em', [_vm._v("Christ in the Old Testament")]), _vm._v(" (New York: Loizeaux, 1950); and Howard A. Hanke, "), _c('em', [_vm._v("Christ and the Church in the Old Testament")]), _vm._v(" (Grand Rapids: Zondervan, 1957).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-19",
      "id": "fn_19"
    }
  }, [_vm._v("19")]), _vm._v(". See James D. G. Dunn, "), _c('em', [_vm._v("Christology in the Making: A New Testament Inquiry into the Origins of the Doctrine of the Incarnation")]), _vm._v(" (London: SCM, 1980), 149–62.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-20",
      "id": "fn_20"
    }
  }, [_vm._v("20")]), _vm._v(". Martin R. DeHaan, "), _c('em', [_vm._v("Portraits of Christ in Genesis")]), _vm._v(" (Grand Rapids: Zondervan, 1966), 32–33.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-21",
      "id": "fn_21"
    }
  }, [_vm._v("21")]), _vm._v(". E.g., Vischer, "), _c('em', [_vm._v("The Witness of the Old Testament to Christ")]), _vm._v(", 75.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-22",
      "id": "fn_22"
    }
  }, [_vm._v("22")]), _vm._v(". Ibid., 132.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-23",
      "id": "fn_23"
    }
  }, [_vm._v("23")]), _vm._v(". From the church fathers to preachers today.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-24",
      "id": "fn_24"
    }
  }, [_vm._v("24")]), _vm._v(". W. L. Wilson, "), _c('em', [_vm._v("Wilson’s Dictionary of Bible Types")]), _vm._v(" (Grand Rapids: Eerdmans, 1957), 15.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-25",
      "id": "fn_25"
    }
  }, [_vm._v("25")]), _vm._v(". Charles Spurgeon, "), _c('em', [_vm._v("Christ in the Old Testament: Sermons on the Foreshadowing of Our Lord in Old Testament History")]), _vm._v(" (London: Passmore & Alabaster, 1899), XLVII, 93–97.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-26",
      "id": "fn_26"
    }
  }, [_vm._v("26")]), _vm._v(". I say “almost” because wisdom literature will still be a major challenge. Because of its unique nature, with wisdom literature one normally cannot use redemptive-historical progression, promise-fulfillment, or typology. Hence one is limited to four ways to Christ: analogy with the teaching of Christ, longitudinal themes, New Testament references, and contrast.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-27",
      "id": "fn_27"
    }
  }, [_vm._v("27")]), _vm._v(". Wenham, "), _c('em', [_vm._v("Genesis 1–15")]), _vm._v(", xxvi. For an enlightening history of source criticism, see Wenham, "), _c('em', [_vm._v("Exploring the Old Testament")]), _vm._v(", 159–85.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-28",
      "id": "fn_28"
    }
  }, [_vm._v("28")]), _vm._v(". Ibid., xxxv. For some of the disagreements among source critics, see, e.g., Wenham, "), _c('em', [_vm._v("Genesis 16–50")]), _vm._v(", 18–19, 79, 203–4, 219–21. Cf. R. K. Harrison, “The Historical and Literary Criticism of the Old Testament,” in "), _c('em', [_vm._v("Biblical Criticism")]), _vm._v(", ed. Harrison et al., 27–29, and Waltke, "), _c('em', [_vm._v("Genesis")]), _vm._v(", 24–29.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-29",
      "id": "fn_29"
    }
  }, [_vm._v("29")]), _vm._v(". Rendsburg, "), _c('em', [_vm._v("Redaction of Genesis")]), _vm._v(", 104. Cf. p. 105, “The standard division of Genesis into J, E, and P strands should be discarded.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-30",
      "id": "fn_30"
    }
  }, [_vm._v("30")]), _vm._v(". Though Rendsburg holds to a fairly late date for the "), _c('em', [_vm._v("redaction")]), _vm._v(" of Genesis (the united monarchy of the tenth century "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C")]), _vm._v(".), he acknowledges that “Genesis is replete with evidence indicating the antiquity of the book.” Ibid., 114. For example, “various customs reflected in Genesis … contradict later Pentateuchal law,” such as Abram marrying his half-sister and Jacob marrying his sister-in-law. Ibid., 115. See pp. 107–20.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-31",
      "id": "fn_31"
    }
  }, [_vm._v("31")]), _vm._v(". Wenham, "), _c('em', [_vm._v("Genesis 1–15")]), _vm._v(", xxxiv.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-32",
      "id": "fn_32"
    }
  }, [_vm._v("32")]), _vm._v(". See my "), _c('em', [_vm._v("Modern Preacher and Ancient Text")]), _vm._v(", 51–67, and my article, “The Value of a Literary Approach for Preaching,” 509–19.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-33",
      "id": "fn_33"
    }
  }, [_vm._v("33")]), _vm._v(". Martin Woudstra, “The "), _c('em', [_vm._v("Toledoth")]), _vm._v(" of the Book of Genesis and Their Redemptive-Historical Significance,” "), _c('em', [_vm._v("CTJ")]), _vm._v(" 5/2 (1970) 187.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-34",
      "id": "fn_34"
    }
  }, [_vm._v("34")]), _vm._v(". “Ten” is the number of fullness; “seven” the number of completion or perfection.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-35",
      "id": "fn_35"
    }
  }, [_vm._v("35")]), _vm._v(". See the note above.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-36",
      "id": "fn_36"
    }
  }, [_vm._v("36")]), _vm._v(". I take the repetition of "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" in 36:9 to be just that, a repetition of 36:1 expanding on the same "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-37",
      "id": "fn_37"
    }
  }, [_vm._v("37")]), _vm._v(". Waltke, "), _c('em', [_vm._v("Genesis")]), _vm._v(", 106.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-38",
      "id": "fn_38"
    }
  }, [_vm._v("38")]), _vm._v(". Ibid. This obvious literary structure of a full number of ten generations from Adam to Noah and ten generations from Shem to Abram should caution interpreters not to use the "), _c('em', [_vm._v("tôlĕdôt")]), _vm._v(" structure to calculate the date of Adam.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-39",
      "id": "fn_39"
    }
  }, [_vm._v("39")]), _vm._v(". The genealogies highlight “the existence of a unique line of ‘seed’ which will eventually become a royal dynasty. Members of this lineage enjoy a special relationship with God who actively provides and sustains each new generation.” Alexander, “Genealogies, Seed and the Compositional Unity of Genesis,” “Covenant and Creation,” "), _c('em', [_vm._v("TynBul")]), _vm._v(" 44/2 (1993) 269–70.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-40",
      "id": "fn_40"
    }
  }, [_vm._v("40")]), _vm._v(". Waltke, "), _c('em', [_vm._v("Genesis")]), _vm._v(", 44.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-41",
      "id": "fn_41"
    }
  }, [_vm._v("41")]), _vm._v(". Mathews, “Genesis,” 141.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-42",
      "id": "fn_42"
    }
  }, [_vm._v("42")]), _vm._v(". Ross, "), _c('em', [_vm._v("Creation and Blessing")]), _vm._v(", 66.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-43",
      "id": "fn_43"
    }
  }, [_vm._v("43")]), _vm._v(". Ibid.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-44",
      "id": "fn_44"
    }
  }, [_vm._v("44")]), _vm._v(". For Abram, see also Genesis 13:14–18; 15:4–5, 13–21; 17:4–8; 18:17–19; 22:15–18. For Isaac, see 26:4, 24; and for Jacob, see 28:13–15; 35:11–12; and 46:3.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-45",
      "id": "fn_45"
    }
  }, [_vm._v("45")]), _vm._v(". In the second millennium "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C")]), _vm._v("., the usual elements in international (Hittite) covenant treaties were: a preamble identifying the king, a historical prologue relating the king’s deeds, covenant stipulations that spelled out the obligations of the vassal to the king, where the treaty was to be kept and when read in public, witnesses, blessings if obedient and curses if disobedient. The book of Deuteronomy is structured on these very elements. See also the structures of Exodus 20 and Joshua 24. See G. E. Mendenhall, “Covenant,” in "), _c('em', [_vm._v("The Interpreter’s Dictionary of the Bible")]), _vm._v(" (Nashville: Abingdon, 1962), 719–20; Meredith G. Kline, "), _c('em', [_vm._v("Treaty of the Great King: The Covenant Structure of Deuteronomy")]), _vm._v(" (Grand Rapids: Eerdmans, 1963), 28–44, and Kitchen, "), _c('em', [_vm._v("On the Reliability of the Old Testament")]), _vm._v(", 283–94.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-46",
      "id": "fn_46"
    }
  }, [_vm._v("46")]), _vm._v(". William Dumbrell, "), _c('em', [_vm._v("Covenant and Creation")]), _vm._v(", 26, argues that the use of "), _c('em', [_vm._v("hăqim bĕrît")]), _vm._v(" instead of "), _c('em', [_vm._v("kārat bĕrît")]), _vm._v(" in Genesis 6:18 “makes it more than likely that … the institution of a covenant is not being referred to but rather its perpetuation.” For a contrary position, see John H. Stek, “ ‘Covenant’ Overload in Reformed Theology,” "), _c('em', [_vm._v("CTJ")]), _vm._v(" 29 (1994) 12–41, esp. 22–24. See also the rejoinder by Craig G. Bartholomew, “Covenant and Creation,” "), _c('em', [_vm._v("CTJ")]), _vm._v(" 30 (1995) 11–33.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-47",
      "id": "fn_47"
    }
  }, [_vm._v("47")]), _vm._v(". For the specific references, see Clines, "), _c('em', [_vm._v("Theme of the Pentateuch")]), _vm._v(", 32–40. See further Van-Gemeren, "), _c('em', [_vm._v("Progress of Redemption")]), _vm._v(", 104–8.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-48",
      "id": "fn_48"
    }
  }, [_vm._v("48")]), _vm._v(". It is first given in Genesis 12:3, repeated to Abraham in 22:18, to Isaac in 26:4, and to Jacob in 28:14. It is implied in Genesis 17:4–6, 16, and remembered in 18:18.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-49",
      "id": "fn_49"
    }
  }, [_vm._v("49")]), _vm._v(". Alexander, “Genealogies, Seed and the Compositional Unity of Genesis,” "), _c('em', [_vm._v("TynBul")]), _vm._v(" 44/2 (1993) 259–60.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-50",
      "id": "fn_50"
    }
  }, [_vm._v("50")]), _vm._v(". Tremper Longman III, "), _c('em', [_vm._v("Literary Approaches to Biblical Interpretation")]), _vm._v(" (Grand Rapids: Zondervan, 1987), 92.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-51",
      "id": "fn_51"
    }
  }, [_vm._v("51")]), _vm._v(". Walter Kaiser argues that God’s “promise-plan” “embraces both testaments in one unified, single plan.” "), _c('em', [_vm._v("Preaching and Teaching from the Old Testament")]), _vm._v(", 38. Cf. his "), _c('em', [_vm._v("Toward an Old Testament Theology.")])]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-52",
      "id": "fn_52"
    }
  }, [_vm._v("52")]), _vm._v(". Coats, "), _c('em', [_vm._v("Genesis")]), _vm._v(", 8–9.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-53",
      "id": "fn_53"
    }
  }, [_vm._v("53")]), _vm._v(". Hamilton, "), _c('em', [_vm._v("Genesis 1–17")]), _vm._v(", 57.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-54",
      "id": "fn_54"
    }
  }, [_vm._v("54")]), _vm._v(". McCartney and Clayton, "), _c('em', [_vm._v("Let the Reader Understand")]), _vm._v(", 212–13.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-55",
      "id": "fn_55"
    }
  }, [_vm._v("55")]), _vm._v(". See Ross, "), _c('em', [_vm._v("Creation and Blessing")]), _vm._v(", 52–54.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-56",
      "id": "fn_56"
    }
  }, [_vm._v("56")]), _vm._v(". Coats, "), _c('em', [_vm._v("Genesis")]), _vm._v(", 319.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-57",
      "id": "fn_57"
    }
  }, [_vm._v("57")]), _vm._v(". See, e.g., von Rad’s struggle to ground Israel’s “sagas” “in the ‘real’ history.” "), _c('em', [_vm._v("Old Testament Theology")]), _vm._v(", I, 108. Cf. his "), _c('em', [_vm._v("Genesis")]), _vm._v(" (rev. ed., 1972), 31–43.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-58",
      "id": "fn_58"
    }
  }, [_vm._v("58")]), _vm._v(". Gowan, "), _c('em', [_vm._v("Reclaiming the Old Testament")]), _vm._v(", 35.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-59",
      "id": "fn_59"
    }
  }, [_vm._v("59")]), _vm._v(". Dillard and Longman, "), _c('em', [_vm._v("Introduction to the Old Testament")]), _vm._v(", 49.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-60",
      "id": "fn_60"
    }
  }, [_vm._v("60")]), _vm._v(". Ibid.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-61",
      "id": "fn_61"
    }
  }, [_vm._v("61")]), _vm._v(". "), _c('em', [_vm._v("Genesis")]), _vm._v(", 29. Cf. Dillard and Longman, "), _c('em', [_vm._v("Introduction to the Old Testament")]), _vm._v(", 49, “If we are speaking of the original intention of the biblical writer(s), the style of the book leaves little space to argue over the obvious conclusion that the author intended it to be read as a work of history that recounts what has taken place in the far-distant past.” Cf. Provan et al., "), _c('em', [_vm._v("A Biblical History of Israel")]), _vm._v(", 111, “One cannot conceive of the original audience as thinking of Abraham as other than a real person, or of his movements from Ur to Haran to Palestine as other than a real journey.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-62",
      "id": "fn_62"
    }
  }, [_vm._v("62")]), _vm._v(". Some prefer “salvation history” to “redemptive history,” but “redemptive history” is more precise in signaling "), _c('em', [_vm._v("how")]), _vm._v(" God saves, namely by redeeming, rescuing and delivering, his creation and creatures from enslavement to Satan (Gen 3:15) and sin (Gen 8:21).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-63",
      "id": "fn_63"
    }
  }, [_vm._v("63")]), _vm._v(". This redemptive-historical approach is not to be identified with the "), _c('em', [_vm._v("Heilsgeschichte")]), _vm._v(" school of von Rad and others who separate "), _c('em', [_vm._v("geschichte")]), _vm._v(" from "), _c('em', [_vm._v("historie")]), _vm._v(" (facts).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-64",
      "id": "fn_64"
    }
  }, [_vm._v("64")]), _vm._v(". In 1898, Troeltsch taught that “the means by which criticism becomes possible is the employment of analogy. The analogy of what takes place before our eyes … is the key to criticism.… Correspondence with normal, customary, or at least repeatedly attested ways of occurrence …, as we know them, is the mark of probability for occurrences which criticism can acknowledge as really having happened” ("), _c('em', [_vm._v("Gesammelte Schriften")]), _vm._v(" [Tübingen: Mohr, 1913], II, 732, my translation). The principle of analogy with "), _c('em', [_vm._v("our")]), _vm._v(" experience of reality places a large question mark behind the historicity of the Genesis narratives where God walks and talks with people. In addition to the principle of analogy, Troeltsch employed the principle of correlation, that is, all events must be understood in terms of their causes and effects (ibid., 732–37). This principle applied in a "), _c('em', [_vm._v("closed")]), _vm._v(" universe allows only for immanent causes and eliminates God as a possible agent in human history. Cf. von Rad, "), _c('em', [_vm._v("Old Testament Theology")]), _vm._v(", II, 418, “The modern view of history … finds it very hard to assume that there is divine action in history. God has no place in its "), _c('em', [_vm._v("schema.")]), _vm._v(" The realization that the men who give us the history of ancient Israel were not only men of faith, but that … their conceptions of history centred completely on God, brings out the tremendous difference between their view of history and the modern scientific one.” For a more detailed analysis of the historical-critical method, see my "), _c('em', [_vm._v("Modern Preacher")]), _vm._v(", 25–36.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-65",
      "id": "fn_65"
    }
  }, [_vm._v("65")]), _vm._v(". See my proposal in "), _c('em', [_vm._v("Modern Preacher")]), _vm._v(", 36–47. See also Grant R. Osborne, “Historical Narrative and Truth in the Bible,” "), _c('em', [_vm._v("JETS")]), _vm._v(" 48/4 (Dec. 2005) 684.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-66",
      "id": "fn_66"
    }
  }, [_vm._v("66")]), _vm._v(". Merrill, “Old Testament History,” 72.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-67",
      "id": "fn_67"
    }
  }, [_vm._v("67")]), _vm._v(". Duane L. Christensen, “Josephus and the Twenty-Two Book Canon of Sacred Scripture,” "), _c('em', [_vm._v("JETS")]), _vm._v(" 29 (1986) 45–46. Cf. Hamilton, "), _c('em', [_vm._v("Genesis 18–50")]), _vm._v(", 709.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-68",
      "id": "fn_68"
    }
  }, [_vm._v("68")]), _vm._v(". For more details on biblical history writing, see my "), _c('em', [_vm._v("Modern Preacher")]), _vm._v(", 82–94 and 191–97. For historical incongruities in Genesis, see LaSor et al., "), _c('em', [_vm._v("Old Testament Survey")]), _vm._v(", 108–9.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-69",
      "id": "fn_69"
    }
  }, [_vm._v("69")]), _vm._v(". Bertil Albrektson, "), _c('em', [_vm._v("History and the Gods")]), _vm._v(" (Lund: Gleerup, 1967), 119.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-70",
      "id": "fn_70"
    }
  }, [_vm._v("70")]), _vm._v(". “It must be admitted that Genesis … is not attempting to be as close as possible to a dispassionate reporting of events. Rather, we have proclamation—with the result that the history is shaped to differing degrees. The point is that the Biblical narrators are concerned not only to tell us the facts but also to guide our perspective of and responses to those events.” Longman, “The Literary Approach to the Study of the Old Testament: Promises and Pitfalls,” "), _c('em', [_vm._v("JETS")]), _vm._v(" 28/4 (1985) 395.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-71",
      "id": "fn_71"
    }
  }, [_vm._v("71")]), _vm._v(". Just as Luke (see Luke 1:1–4) used sources to write his Gospel. See Numbers 21:14 about “the Book of the Wars of the L"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("ORD")]), _vm._v(".”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-72",
      "id": "fn_72"
    }
  }, [_vm._v("72")]), _vm._v(". Merrill, “Old Testament History,” 77.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-73",
      "id": "fn_73"
    }
  }, [_vm._v("73")]), _vm._v(". Mathews, “Preaching in the Pentateuch,” 268. Cf. Ross, "), _c('em', [_vm._v("Creation and Blessing")]), _vm._v(", 62, “We conclude, with others, that the two traditions must be parallel traditions, perhaps going back to one source.” Cf. Kitchen, "), _c('em', [_vm._v("On the Reliability of the Old Testament")]), _vm._v(", 422–27, 447.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-74",
      "id": "fn_74"
    }
  }, [_vm._v("74")]), _vm._v(". A convenient place to find these myths in English translation is Hallo, ed., "), _c('em', [_vm._v("The Context of Scripture:")]), _vm._v(" Vol. I, "), _c('em', [_vm._v("Enuma Elish")]), _vm._v(", 390–402; "), _c('em', [_vm._v("Atrahasis")]), _vm._v(", 450–53; "), _c('em', [_vm._v("Gilgamesh")]), _vm._v(", 458–60; "), _c('em', [_vm._v("Eridu Genesis")]), _vm._v(", 513–15.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-75",
      "id": "fn_75"
    }
  }, [_vm._v("75")]), _vm._v(". "), _c('em', [_vm._v("Atrahasis")]), _vm._v(", lines 190–97.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-76",
      "id": "fn_76"
    }
  }, [_vm._v("76")]), _vm._v(". Mathews, “Preaching in the Pentateuch,” 269.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-77",
      "id": "fn_77"
    }
  }, [_vm._v("77")]), _vm._v(". Ross, "), _c('em', [_vm._v("Creation and Blessing")]), _vm._v(", 62. Cf. Kitchen, "), _c('em', [_vm._v("On the Reliability of the Old Testament")]), _vm._v(", 368–71.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-78",
      "id": "fn_78"
    }
  }, [_vm._v("78")]), _vm._v(". Hamilton, "), _c('em', [_vm._v("Genesis 1–17")]), _vm._v(", 62. Cf. Kitchen, "), _c('em', [_vm._v("On the Reliability of the Old Testament")]), _vm._v(", 324–28, 352–60.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-79",
      "id": "fn_79"
    }
  }, [_vm._v("79")]), _vm._v(". Sailhamer, "), _c('em', [_vm._v("Pentateuch as Narrative")]), _vm._v(", 23–24. Cf. Wenham, "), _c('em', [_vm._v("Genesis 16–50")]), _vm._v(", xx-xxx. Cf. Kitchen, "), _c('em', [_vm._v("On the Reliability of the Old Testament")]), _vm._v(", 365, “In terms of content, these narratives give a picture of real human life as lived by West Semitic pastoralists, derived mainly from conditions observable in the early second millennium, with a very moderate amount of minor retouches in at least three later periods.” See also the evidence gathered by Kitchen, “The Patriarchal Age: Myth or History?” "), _c('em', [_vm._v("BAR")]), _vm._v(", Mar./Apr. 1995, 48–57.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-80",
      "id": "fn_80"
    }
  }, [_vm._v("80")]), _vm._v(". Francisco, “Preaching from the Primeval Narratives of Genesis,” 20.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-81",
      "id": "fn_81"
    }
  }, [_vm._v("81")]), _vm._v(". See my "), _c('em', [_vm._v("Modern Preacher")]), _vm._v(", 197–213; Waltke, "), _c('em', [_vm._v("Genesis")]), _vm._v(", 31–43; Osborne, "), _c('em', [_vm._v("Hermeneutical Spiral")]), _vm._v(", 154–64; Mathewson, "), _c('em', [_vm._v("The Art of Preaching Old Testament Narrative")]), _vm._v(", 44–66.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-82",
      "id": "fn_82"
    }
  }, [_vm._v("82")]), _vm._v(". “Many literary critics radically deny any historical element in reading a text.” Osborne, "), _c('em', [_vm._v("Hermeneutical Spiral")]), _vm._v(", 164. See also my “The Value of a Literary Approach for Preaching,” 513–14.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-83",
      "id": "fn_83"
    }
  }, [_vm._v("83")]), _vm._v(". Ellingsen, "), _c('em', [_vm._v("The Integrity of Biblical Narrative")]), _vm._v(" (Minneapolis: Fortress, 1990), 68 and 92.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-84",
      "id": "fn_84"
    }
  }, [_vm._v("84")]), _vm._v(". Osborne, "), _c('em', [_vm._v("Hermeneutical Spiral")]), _vm._v(", 168. See also his “Historical Narrative and Truth in the Bible,” "), _c('em', [_vm._v("JETS")]), _vm._v(" 48/4 (Dec. 2005) 673–88. Cf. Philips Long, “Reading the Old Testament as Literature,” 110–11, “To leave aside historical concerns entirely and interminably is misguided in dealing with texts that appear to make historical truth claims—as misguided as focusing on the artistry of a portrait to the exclusion of any interest in the "), _c('em', [_vm._v("historical")]), _vm._v(" subject of which the portrait is a representation.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-85",
      "id": "fn_85"
    }
  }, [_vm._v("85")]), _vm._v(". Goldingay, “Patriarchs in Scripture and History,” 39–40.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-86",
      "id": "fn_86"
    }
  }, [_vm._v("86")]), _vm._v(". Sailhamer, "), _c('em', [_vm._v("Pentateuch as Narrative")]), _vm._v(", 16.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-87",
      "id": "fn_87"
    }
  }, [_vm._v("87")]), _vm._v(". Ibid., 14.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-88",
      "id": "fn_88"
    }
  }, [_vm._v("88")]), _vm._v(". See my "), _c('em', [_vm._v("Modern Preacher")]), _vm._v(", 194–97. Cf. Goldingay, “Patriarchs in Scripture and History,” 36: “While the historicity of the events is not sufficient evidence of the truth of the narrative’s interpretation, it is necessary evidence of its truth. The narrative builds its interpretation on the factuality of the patriarchal events, so that without this factuality, faith in Yahweh as the giver of blessing, the one who keeps his promises, the God of grace, and so on, may be true but is nevertheless groundless.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-89",
      "id": "fn_89"
    }
  }, [_vm._v("89")]), _vm._v(". Kitchen, “The Patriarchal Age: Myth or History?” "), _c('em', [_vm._v("BAR")]), _vm._v(", Mar./Apr. 1995, 50. See Wenham, "), _c('em', [_vm._v("Exploring the Old Testament")]), _vm._v(", 30, for the most recent evidence of a cataclysmic flood at the Black Sea basin. See ibid., 38–39, on the historicity of the patriarchal stories, and 53 on the historicity of the Joseph story.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-90",
      "id": "fn_90"
    }
  }, [_vm._v("90")]), _vm._v(". By “general historical reliability” I mean that there is a real historical core to these narratives, but, given the literary shaping we have noted above, it would be contrary to the author’s intention to press for historical accuracy in the details.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-91",
      "id": "fn_91"
    }
  }, [_vm._v("91")]), _vm._v(". Dillard and Longman, "), _c('em', [_vm._v("Introduction to the Old Testament")]), _vm._v(", 39.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-92",
      "id": "fn_92"
    }
  }, [_vm._v("92")]), _vm._v(". Waltke, "), _c('em', [_vm._v("Genesis")]), _vm._v(", 22–28.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-93",
      "id": "fn_93"
    }
  }, [_vm._v("93")]), _vm._v(". Cf. Stephen’s speech to the Council, “So Moses was instructed in all the wisdom of the Egyptians …” (Acts 7:22).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-94",
      "id": "fn_94"
    }
  }, [_vm._v("94")]), _vm._v(". “Write these words; in accordance with these words I have made a covenant with you and with Israel.… And he wrote on the tablets the words of the covenant, the ten commandments” (Exod 34:27–28; cf. 24:4 and Deut 4:13; 5:22; 31:9, 22, 24).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-95",
      "id": "fn_95"
    }
  }, [_vm._v("95")]), _vm._v(". Cf. Josh 8:31; see also 1 Kings 2:3; 2 Kings 14:6; 21:8.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-96",
      "id": "fn_96"
    }
  }, [_vm._v("96")]), _vm._v(". Waltke, "), _c('em', [_vm._v("Genesis")]), _vm._v(", 22, with references to 2 Chron 25:4; 35:12; Ezra 3:2; 7:6; and Neh 8:1 See also Ezra 6:18 and Neh 13:1. See ibid., 22–24, for more arguments for Mosaic authorship of “Ur-Genesis.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-97",
      "id": "fn_97"
    }
  }, [_vm._v("97")]), _vm._v(". A concordance will give a multitude of New Testament references to verses that identify Moses and “the law,” the first books of the Bible. See, e.g., Luke 24:27, 44 and John 5:46.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-98",
      "id": "fn_98"
    }
  }, [_vm._v("98")]), _vm._v(". Ibid., 23. Cf. Kitchen, "), _c('em', [_vm._v("On the Reliability of the Old Testament")]), _vm._v(", 295–99, 306.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-99",
      "id": "fn_99"
    }
  }, [_vm._v("99")]), _vm._v(". Sailhamer, "), _c('em', [_vm._v("Genesis")]), _vm._v(", 5.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-100",
      "id": "fn_100"
    }
  }, [_vm._v("100")]), _vm._v(". As we search for answers to these foundational questions, we should remember that the message of the preaching-text does not change with different historical circumstances but that the goal and consequently the application of that message may change.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-101",
      "id": "fn_101"
    }
  }, [_vm._v("101")]), _vm._v(". For example, Norman L. Geisler, "), _c('em', [_vm._v("A Popular Survey of the Old Testament")]), _vm._v(" (Grand Rapids: Baker, 1977), 38–40.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-102",
      "id": "fn_102"
    }
  }, [_vm._v("102")]), _vm._v(". Sailhamer finds the ten words also repeated in God recreating the world (Gen 6:7–9:17). See his "), _c('em', [_vm._v("Genesis")]), _vm._v(", 94.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-103",
      "id": "fn_103"
    }
  }, [_vm._v("103")]), _vm._v(". Wenham, “The Face at the Bottom of the Well,” 206.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-104",
      "id": "fn_104"
    }
  }, [_vm._v("104")]), _vm._v(". Sailhamer, "), _c('em', [_vm._v("The Pentateuch as Narrative")]), _vm._v(", 6. See also Merrill, “Old Testament History,” 72–75.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-105",
      "id": "fn_105"
    }
  }, [_vm._v("105")]), _vm._v(". Moab is also the setting claimed for the book of Deuteronomy (see Deut 1:5).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-106",
      "id": "fn_106"
    }
  }, [_vm._v("106")]), _vm._v(". Cf. Num 13:28–33.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-107",
      "id": "fn_107"
    }
  }, [_vm._v("107")]), _vm._v(". Cf. Richard Pratt, "), _c('em', [_vm._v("He Gave Us Stories")]), _vm._v(", 282, “Israel could be confident in conquest because: 1) God’s actions in the primeval history reveal his plan to give them possession of Canaan; 2) God promised, led, and protected the patriarchs, and he will do the same for the nation as they move toward Canaan; 3) God ordered the twelve tribes in the days of Joseph in order to bring them to the land.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-108",
      "id": "fn_108"
    }
  }, [_vm._v("108")]), _vm._v(". Merrill, “Old Testament History,” 74.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-109",
      "id": "fn_109"
    }
  }, [_vm._v("109")]), _vm._v(". LaSor et al., "), _c('em', [_vm._v("Old Testament Survey")]), _vm._v(", 60.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-110",
      "id": "fn_110"
    }
  }, [_vm._v("110")]), _vm._v(". Ibid.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-111",
      "id": "fn_111"
    }
  }, [_vm._v("111")]), _vm._v(". See Goldingay, “The Patriarchs in Scripture and History,” 30–33. According to P. Wayne Townsend, “Genesis assumes the exodus from Egypt and conquest of Canaan and how it uses the Sinai code.” See his “Eve’s Answer to the Serpent,” "), _c('em', [_vm._v("CTJ")]), _vm._v(" 33 (1998) 402.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-112",
      "id": "fn_112"
    }
  }, [_vm._v("112")]), _vm._v(". Ibid., 26.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-113",
      "id": "fn_113"
    }
  }, [_vm._v("113")]), _vm._v(". Ibid., 33. See p. 41, n. 19, “ ‘Chaldeans’ is a term that strictly belongs only to the first millennium (cf. its prominence in Isaiah, Jeremiah, etc.), and in Genesis 11 it may deliberately make the point that Israel has returned to the land that Abram was called from.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-114",
      "id": "fn_114"
    }
  }, [_vm._v("114")]), _vm._v(". See my "), _c('em', [_vm._v("Modern Preacher")]), _vm._v(", 132–33.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-115",
      "id": "fn_115"
    }
  }, [_vm._v("115")]), _vm._v(". Dan Via, "), _c('em', [_vm._v("The Parables: Their Literary and Existential Dimension")]), _vm._v(" (Philadelphia: Fortress, 1967), 96–97.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-116",
      "id": "fn_116"
    }
  }, [_vm._v("116")]), _vm._v(". The theme of a literary unit is the only objective point of control when the precise historical reason for writing is uncertain. Therefore the theme is primary in determining what the author’s objectives may have been in writing this message.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-117",
      "id": "fn_117"
    }
  }, [_vm._v("117")]), _vm._v(". See my “Application in Preaching Old Testament Texts,” 233–44. Cf. Walter Russell, “Literary Forms,” 281–98; and Richard Pratt, "), _c('em', [_vm._v("He Gave Us Stories")]), _vm._v(", 307–402.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-118",
      "id": "fn_118"
    }
  }, [_vm._v("118")]), _vm._v(". See my "), _c('em', [_vm._v("Modern Preacher")]), _vm._v(", 224–26; Eugene Lowry, "), _c('em', [_vm._v("The Homiletical Plot")]), _vm._v(" (Atlanta: John Knox, 1980), 88–95; Eugene Lowry, "), _c('em', [_vm._v("How to Preach a Parable: Designs for Narrative Sermons")]), _vm._v(" (Nashville: Abingdon, 1989), 38–41; Calvin Miller, “Narrative Preaching,” in "), _c('em', [_vm._v("Handbook of Contemporary Preaching")]), _vm._v(" (ed. Michael Duduit; Nashville: Broadman, 1992), 103–16; David C. Deuel, “Expository Preaching from Old Testament Narrative,” in "), _c('em', [_vm._v("Rediscovering Expository Preaching")]), _vm._v(" (ed. John MacArthur; Dallas: Word, 1992), 273–87; and Mathewson, "), _c('em', [_vm._v("The Art of Preaching Old Testament Narrative.")])]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-119",
      "id": "fn_119"
    }
  }, [_vm._v("119")]), _vm._v(". For further elucidation on many of these characteristics, see Mark Galli and Graig Brian Larson, "), _c('em', [_vm._v("Preaching that Connects: Using the Techniques of Journalists to Add Impact to Your Sermons")]), _vm._v(" (Grand Rapids: Zondervan, 1994).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn-120",
      "id": "fn_120"
    }
  }, [_vm._v("120")]), _vm._v(". Narration in the present tense is more vivid and immediate than the past tense. Unfortunately, when preachers seek to tell biblical stories in the present tense, they are hampered in quoting the text itself since translators usually translate the Hebrew “perfect tense” as an English past tense.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-71c54bf9","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/010_Chapter01.vue
// module id = 84
// module chunks = 0